import React, { useEffect, useState, Fragment, Component } from 'react';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, MapPin, Sparkles, Check, Navigation, GripVertical, Plus, Minus, Search, ArrowRight, Clock, CheckCircle, X, Edit2, AlertTriangle, Trash2, ChevronRight as ChevronRightIcon } from 'lucide-react';
import { JobDetailModal } from './modals/JobDetailModal';
import { MapContainer, TileLayer, Marker, Polyline, useMap, ZoomControl, Circle } from 'react-leaflet';
import L from 'leaflet';
// --- Types ---
type Step = 'calendar-setup' | 'date-selected' | 'search-prompt' | 'loading' | 'results' | 'custom-compose' | 'browse-opportunities';
type UrgencyLevel = 'Bassa' | 'Media' | 'Alta';
interface OpportunityItem {
  id: number;
  address: string;
  timeWindow: string;
  sqm: number;
  urgency: UrgencyLevel;
  impact: 'Basso' | 'Medio' | 'Alto';
  serviceType: 'Pulizia Standard' | 'Pulizia Rapida';
  lat: number;
  lng: number;
  originalTimeWindow?: string; // To preserve duration when recalculating
}
interface OpportunityGroup {
  id: number;
  items: OpportunityItem[];
  isCustom?: boolean;
}
interface CalendarEvent {
  id: number;
  address: string;
  time: string;
  type: string;
  lat: number;
  lng: number;
  confirmed?: boolean;
  source?: 'google' | 'outlook';
}
type TimelineEntry = {
  type: 'fixed';
  event: CalendarEvent;
} | {
  type: 'opportunity';
  item: OpportunityItem;
  letter: string;
};
// --- Mock Data ---
const mockCalendarEvents: Record<string, CalendarEvent[]> = {
  '2026-02-10': [{
    id: 999,
    address: 'Via Cola di Rienzo 28',
    time: '10:00 - 12:00',
    type: 'Riparazione Tapparella',
    lat: 41.9085,
    lng: 12.4665,
    confirmed: false,
    source: 'google'
  }],
  '2026-02-12': [{
    id: 998,
    address: 'Piazza Cavour 15',
    time: '09:00 - 11:00',
    type: 'Sostituzione Guarnizione',
    lat: 41.9055,
    lng: 12.4685,
    confirmed: false,
    source: 'google'
  }, {
    id: 997,
    address: 'Via Orazio 4',
    time: '14:00 - 16:00',
    type: 'Manutenzione Caldaia',
    lat: 41.9075,
    lng: 12.4645,
    confirmed: false,
    source: 'outlook'
  }]
};
// Mock available opportunities per date
const mockAvailableItems: OpportunityItem[] = [{
  id: 801,
  address: 'Via Crescenzio 58',
  timeWindow: '09:00 - 11:00',
  sqm: 70,
  urgency: 'Bassa',
  impact: 'Basso',
  serviceType: 'Riparazione Serratura',
  lat: 41.9078,
  lng: 12.466
}, {
  id: 802,
  address: 'Via Terenzio 3',
  timeWindow: '14:00 - 14:30',
  sqm: 55,
  urgency: 'Media',
  impact: 'Medio',
  serviceType: 'Controllo Caldaia',
  lat: 41.9072,
  lng: 12.4672
}, {
  id: 803,
  address: 'Via Germanico 20',
  timeWindow: '11:30 - 13:30',
  sqm: 80,
  urgency: 'Alta',
  impact: 'Alto',
  serviceType: 'Sostituzione Rubinetto',
  lat: 41.9095,
  lng: 12.462
}, {
  id: 804,
  address: 'Via Ovidio 10',
  timeWindow: '16:00 - 18:00',
  sqm: 95,
  urgency: 'Media',
  impact: 'Medio',
  serviceType: 'Tinteggiatura Parete',
  lat: 41.906,
  lng: 12.4695
}, {
  id: 805,
  address: 'Via Virgilio 4',
  timeWindow: '10:00 - 10:30',
  sqm: 40,
  urgency: 'Bassa',
  impact: 'Basso',
  serviceType: 'Reset Router Wi-Fi',
  lat: 41.905,
  lng: 12.467
}];
// Updated mock packages (removed duplicates of fixed events)
const initialMockPackages: OpportunityGroup[] = [{
  id: 1,
  items: [{
    id: 102,
    address: 'Via Ezio 12',
    timeWindow: '12:30 - 13:00',
    sqm: 45,
    urgency: 'Alta',
    impact: 'Alto',
    serviceType: 'Sblocco Scarico',
    lat: 41.9095,
    lng: 12.4685
  }, {
    id: 103,
    address: 'Via Tacito 50',
    timeWindow: '14:00 - 16:00',
    sqm: 90,
    urgency: 'Bassa',
    impact: 'Basso',
    serviceType: 'Montaggio Mensole',
    lat: 41.907,
    lng: 12.469
  }]
}, {
  id: 2,
  items: [{
    id: 202,
    address: 'Via Germanico 84',
    timeWindow: '12:15 - 12:45',
    sqm: 50,
    urgency: 'Media',
    impact: 'Medio',
    serviceType: 'Sostituzione Lampadine',
    lat: 41.908,
    lng: 12.464
  }, {
    id: 203,
    address: 'Viale Giulio Cesare 30',
    timeWindow: '13:45 - 15:45',
    sqm: 100,
    urgency: 'Alta',
    impact: 'Alto',
    serviceType: 'Riparazione Persiana',
    lat: 41.909,
    lng: 12.462
  }]
}, {
  id: 3,
  items: [{
    id: 302,
    address: 'Via dei Gracchi 120',
    timeWindow: '12:30 - 14:30',
    sqm: 80,
    urgency: 'Bassa',
    impact: 'Basso',
    serviceType: 'Manutenzione Condizionatore',
    lat: 41.9075,
    lng: 12.4655
  }, {
    id: 303,
    address: 'Via Boezio 15',
    timeWindow: '15:00 - 15:30',
    sqm: 40,
    urgency: 'Media',
    impact: 'Medio',
    serviceType: 'Verifica Presa Elettrica',
    lat: 41.9065,
    lng: 12.4675
  }]
}];
// Mock available opportunities per date (MUST be after initialMockPackages and mockAvailableItems)
const mockOpportunitiesByDate: Record<string, OpportunityItem[]> = {
  '2026-02-10': [...initialMockPackages.flatMap(g => g.items), ...mockAvailableItems],
  '2026-02-11': [{
    id: 901,
    address: 'Via Ottaviano 32',
    timeWindow: '09:00 - 11:00',
    sqm: 75,
    urgency: 'Alta',
    impact: 'Alto',
    serviceType: 'Riparazione Tapparella',
    lat: 41.9095,
    lng: 12.4585
  }, {
    id: 902,
    address: 'Via Candia 60',
    timeWindow: '11:30 - 12:00',
    sqm: 55,
    urgency: 'Media',
    impact: 'Medio',
    serviceType: 'Sostituzione Guarnizione',
    lat: 41.9105,
    lng: 12.4565
  }, {
    id: 903,
    address: 'Via Leone IV 20',
    timeWindow: '14:00 - 16:00',
    sqm: 90,
    urgency: 'Bassa',
    impact: 'Basso',
    serviceType: 'Installazione Zanzariera',
    lat: 41.9075,
    lng: 12.4575
  }, {
    id: 904,
    address: 'Viale delle Milizie 14',
    timeWindow: '10:00 - 10:30',
    sqm: 60,
    urgency: 'Alta',
    impact: 'Alto',
    serviceType: 'Controllo Perdita Gas',
    lat: 41.9115,
    lng: 12.4605
  }, {
    id: 905,
    address: 'Via Barletta 8',
    timeWindow: '15:00 - 17:00',
    sqm: 85,
    urgency: 'Media',
    impact: 'Medio',
    serviceType: 'Riparazione Porta',
    lat: 41.9065,
    lng: 12.4555
  }, {
    id: 906,
    address: 'Via Cipro 44',
    timeWindow: '13:00 - 13:30',
    sqm: 50,
    urgency: 'Bassa',
    impact: 'Basso',
    serviceType: 'Fissaggio Battiscopa',
    lat: 41.9085,
    lng: 12.4545
  }],
  '2026-02-12': [{
    id: 911,
    address: 'Via Fabio Massimo 18',
    timeWindow: '08:00 - 10:00',
    sqm: 80,
    urgency: 'Alta',
    impact: 'Alto',
    serviceType: 'Riparazione Scarico Bagno',
    lat: 41.9065,
    lng: 12.4665
  }, {
    id: 912,
    address: 'Via Pompeo Magno 3',
    timeWindow: '11:30 - 12:00',
    sqm: 45,
    urgency: 'Media',
    impact: 'Medio',
    serviceType: 'Sostituzione Maniglia',
    lat: 41.9095,
    lng: 12.4635
  }, {
    id: 913,
    address: 'Via Cicerone 50',
    timeWindow: '16:30 - 18:30',
    sqm: 100,
    urgency: 'Bassa',
    impact: 'Basso',
    serviceType: 'Verniciatura Infissi',
    lat: 41.9055,
    lng: 12.4655
  }, {
    id: 914,
    address: 'Via Marcantonio Colonna 7',
    timeWindow: '12:00 - 12:30',
    sqm: 65,
    urgency: 'Alta',
    impact: 'Alto',
    serviceType: 'Spurgo Lavandino',
    lat: 41.9075,
    lng: 12.4615
  }],
  '2026-02-13': [{
    id: 921,
    address: 'Via Settembrini 22',
    timeWindow: '09:00 - 11:00',
    sqm: 70,
    urgency: 'Media',
    impact: 'Medio',
    serviceType: 'Manutenzione Caldaia',
    lat: 41.9088,
    lng: 12.4628
  }, {
    id: 922,
    address: 'Via Silla 10',
    timeWindow: '11:30 - 12:00',
    sqm: 55,
    urgency: 'Alta',
    impact: 'Alto',
    serviceType: 'Riparazione Citofono',
    lat: 41.9098,
    lng: 12.4598
  }, {
    id: 923,
    address: 'Via Andrea Doria 48',
    timeWindow: '14:00 - 16:00',
    sqm: 95,
    urgency: 'Bassa',
    impact: 'Basso',
    serviceType: 'Installazione Mensole',
    lat: 41.9108,
    lng: 12.4568
  }, {
    id: 924,
    address: 'Via Properzio 5',
    timeWindow: '16:30 - 17:00',
    sqm: 40,
    urgency: 'Media',
    impact: 'Medio',
    serviceType: 'Controllo Impianto Elettrico',
    lat: 41.9058,
    lng: 12.4678
  }, {
    id: 925,
    address: 'Via Virgilio 15',
    timeWindow: '10:00 - 12:00',
    sqm: 80,
    urgency: 'Alta',
    impact: 'Alto',
    serviceType: 'Riparazione Perdita Acqua',
    lat: 41.9048,
    lng: 12.4668
  }]
};
// --- Helpers ---
const months = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];
// NEW HELPERS
function getMonday(d: Date): Date {
  const date = new Date(d);
  const day = date.getDay();
  const diff = date.getDate() - day + (day === 0 ? -6 : 1); // adjust when day is sunday
  return new Date(date.setDate(diff));
}
function getWeekDays(startDate: Date): Date[] {
  const days = [];
  for (let i = 0; i < 7; i++) {
    const day = new Date(startDate);
    day.setDate(startDate.getDate() + i);
    days.push(day);
  }
  return days;
}
function isSameDay(d1: Date, d2: Date): boolean {
  return d1.getFullYear() === d2.getFullYear() && d1.getMonth() === d2.getMonth() && d1.getDate() === d2.getDate();
}
const getDaysInMonth = (year: number, month: number) => new Date(year, month + 1, 0).getDate();
const getFirstDayOfMonth = (year: number, month: number) => {
  let day = new Date(year, month, 1).getDay();
  return day === 0 ? 6 : day - 1;
};
// Time Helpers
function parseTime(timeStr: string): number {
  const [hours, minutes] = timeStr.split(':').map(Number);
  return hours * 60 + minutes;
}
function formatTime(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = Math.floor(minutes % 60);
  return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
}
function getDuration(timeWindow: string): number {
  const [start, end] = timeWindow.split(' - ');
  return parseTime(end) - parseTime(start);
}
function formatDuration(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}
// Geo helpers
function bearing(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const y = Math.sin(dLng) * Math.cos(lat2 * Math.PI / 180);
  const x = Math.cos(lat1 * Math.PI / 180) * Math.sin(lat2 * Math.PI / 180) - Math.sin(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.cos(dLng);
  return (Math.atan2(y, x) * 180 / Math.PI + 360) % 360;
}
function interpolate(lat1: number, lng1: number, lat2: number, lng2: number, t: number): [number, number] {
  return [lat1 + (lat2 - lat1) * t, lng1 + (lng2 - lng1) * t];
}
function haversineDistance(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371e3; // metres
  const φ1 = lat1 * Math.PI / 180;
  const φ2 = lat2 * Math.PI / 180;
  const Δφ = (lat2 - lat1) * Math.PI / 180;
  const Δλ = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) + Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}
// Round up to nearest 5 minutes (e.g. 2→5, 6→10, 11→15)
function roundUpTo5(minutes: number): number {
  return Math.ceil(minutes / 5) * 5;
}
// Icon Factories
function createLetterIcon(letter: string, isRemove: boolean = false): L.DivIcon {
  const size = 32;
  const html = `
    <div style="position: relative; width: ${size}px; height: ${size}px;">
      <div style="
        width: ${size}px; height: ${size}px;
        background: #2E8C7E;
        color: white;
        border: 2px solid white;
        border-radius: 50%;
        display: flex; align-items: center; justify-content: center;
        font-weight: 700;
        font-family: 'Mulish', sans-serif;
        box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
      ">
        ${letter}
      </div>
      ${isRemove ? `
      <div style="
        position: absolute; top: -4px; right: -4px;
        width: 16px; height: 16px;
        background: #ef4444;
        border-radius: 50%;
        display: flex; align-items: center; justify-content: center;
        border: 1px solid white;
      ">
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="4" stroke-linecap="round" stroke-linejoin="round">
          <line x1="5" y1="12" x2="19" y2="12"></line>
        </svg>
      </div>` : ''}
    </div>
  `;
  return L.divIcon({
    html,
    className: '',
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2]
  });
}
function createPlusIcon(): L.DivIcon {
  const size = 32;
  const html = `
    <div style="
      width: ${size}px; height: ${size}px;
      background: white;
      color: #1E365C;
      border: 2px solid #1E365C;
      border-radius: 50%;
      display: flex; align-items: center; justify-content: center;
      box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
      transition: all 0.2s;
    ">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
        <line x1="12" y1="5" x2="12" y2="19"></line>
        <line x1="5" y1="12" x2="19" y2="12"></line>
      </svg>
    </div>
  `;
  return L.divIcon({
    html,
    className: 'hover:scale-110',
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2]
  });
}
function createExistingEventIcon(): L.DivIcon {
  const size = 40;
  const html = `
    <div style="
      width: ${size}px; height: ${size}px;
      background: #1E365C;
      color: white;
      border: 3px solid white;
      border-radius: 50%;
      display: flex; align-items: center; justify-content: center;
      box-shadow: 0 4px 12px rgba(30,54,92,0.3);
    ">
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
        <line x1="16" y1="2" x2="16" y2="6"></line>
        <line x1="8" y1="2" x2="8" y2="6"></line>
        <line x1="3" y1="10" x2="21" y2="10"></line>
      </svg>
    </div>
  `;
  return L.divIcon({
    html,
    className: '',
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2]
  });
}
function createArrowheadIcon(angleDeg: number): L.DivIcon {
  const size = 16;
  const html = `
    <div style="
      width: ${size}px; height: ${size}px;
      display: flex; align-items: center; justify-content: center;
    ">
      <svg width="${size}" height="${size}" viewBox="0 0 16 16" style="transform: rotate(${angleDeg}deg);">
        <polygon points="4,2 14,8 4,14" fill="#2E8C7E" />
      </svg>
    </div>
  `;
  return L.divIcon({
    html,
    className: '',
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2]
  });
}
// Zone geo data
const zoneGeoData: Record<string, {
  lat: number;
  lng: number;
  radius: number;
}> = {
  Prati: {
    lat: 41.9085,
    lng: 12.465,
    radius: 650
  },
  'Centro Storico': {
    lat: 41.8986,
    lng: 12.4769,
    radius: 800
  },
  Testaccio: {
    lat: 41.876,
    lng: 12.475,
    radius: 500
  },
  Trastevere: {
    lat: 41.887,
    lng: 12.469,
    radius: 600
  }
};
function createAddressMarkerIcon(): L.DivIcon {
  const size = 40;
  const html = `
    <div style="
      width: ${size}px; height: ${size}px;
      display: flex; align-items: center; justify-content: center;
    ">
      <div style="
        width: 36px; height: 36px;
        background: #1E365C;
        color: white;
        border: 3px solid white;
        border-radius: 50%;
        display: flex; align-items: center; justify-content: center;
        box-shadow: 0 4px 12px rgba(30,54,92,0.35);
      ">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
          <circle cx="12" cy="10" r="3"></circle>
        </svg>
      </div>
    </div>
  `;
  return L.divIcon({
    html,
    className: '',
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2]
  });
}
// --- Sub-Components ---
function MapResizer() {
  const map = useMap();
  useEffect(() => {
    const timer = setTimeout(() => map.invalidateSize(), 200);
    return () => clearTimeout(timer);
  }, [map]);
  return null;
}
function MapController({
  bounds
}: {
  bounds: L.LatLngBoundsExpression | null;
}) {
  const map = useMap();
  useEffect(() => {
    if (bounds) {
      map.fitBounds(bounds, {
        padding: [80, 80],
        duration: 1.5,
        animate: true
      });
    }
  }, [bounds, map]);
  return null;
}
function UrgencyBadgeComponent({
  level
}: {
  level: UrgencyLevel;
}) {
  let colors = '';
  switch (level) {
    case 'Bassa':
      colors = 'bg-green-50 text-green-700 border-green-100';
      break;
    case 'Media':
      colors = 'bg-amber-50 text-amber-700 border-amber-100';
      break;
    case 'Alta':
      colors = 'bg-red-50 text-red-700 border-red-100';
      break;
  }
  return <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border ${colors}`} data-id="element-1753">
      <AlertTriangle size={9} data-id="element-1754" />
      Urg. {level}
    </span>;
}
function ImpactBadgeComponent({
  level
}: {
  level: 'Basso' | 'Medio' | 'Alto';
}) {
  let colors = '';
  switch (level) {
    case 'Basso':
      colors = 'bg-blue-50 text-blue-700 border-blue-100';
      break;
    case 'Medio':
      colors = 'bg-amber-50 text-amber-700 border-amber-100';
      break;
    case 'Alto':
      colors = 'bg-red-50 text-red-700 border-red-100';
      break;
  }
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium border ${colors}`} data-id="element-1755">
      Compl. {level}
    </span>;
}
// --- Main Page ---
export function PlannerPage({
  initialView,
  onViewChange
}: {
  initialView?: 'calendar' | 'map';
  onViewChange?: (view: 'calendar' | 'map') => void;
} = {}) {
  // State
  const [step, setStep] = useState<Step>(initialView === 'map' ? 'browse-opportunities' : 'calendar-setup');
  const [connectedCalendars, setConnectedCalendars] = useState<{
    google: boolean;
    outlook: boolean;
  }>({
    google: false,
    outlook: false
  });
  const calendarConnected = connectedCalendars.google || connectedCalendars.outlook;
  const [showGoogleAuthModal, setShowGoogleAuthModal] = useState(false);
  const [showOutlookAuthModal, setShowOutlookAuthModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date(2026, 1, 10)); // Feb 10, 2026
  // NEW STATE FOR WEEKLY CALENDAR
  const [weekStartDate, setWeekStartDate] = useState<Date>(getMonday(new Date(2026, 1, 10)));
  const [slotPopover, setSlotPopover] = useState<{
    dayDate: Date;
    startMinutes: number;
    endMinutes: number;
    anchorRect?: DOMRect;
  } | null>(null);
  const [selectedGroupIndex, setSelectedGroupIndex] = useState(0);
  const [packages, setPackages] = useState<OpportunityGroup[]>(initialMockPackages);
  // Location Selection State
  const [locationMode, setLocationMode] = useState<'zona' | 'indirizzo'>('zona');
  const [inputLocation, setInputLocation] = useState('');
  const [selectedZone, setSelectedZone] = useState<string | null>(null);
  // Detail Modal State
  const [selectedJobForDetail, setSelectedJobForDetail] = useState<OpportunityItem | null>(null);
  // Composition State
  const [customGroupItems, setCustomGroupItems] = useState<OpportunityItem[]>([]);
  const [availableItems, setAvailableItems] = useState<OpportunityItem[]>(mockAvailableItems);
  // Browse opportunities state
  const [browseItems, setBrowseItems] = useState<OpportunityItem[]>([]);
  const [browseSearchQuery, setBrowseSearchQuery] = useState('');
  const [showDistanceWarning, setShowDistanceWarning] = useState(false);
  const [showAiModal, setShowAiModal] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  // Map State
  const [mapBounds, setMapBounds] = useState<L.LatLngBoundsExpression | null>(null);
  // Confirmed Location State (zone or address from initial steps)
  const [confirmedLocation, setConfirmedLocation] = useState<{
    type: 'zona' | 'indirizzo';
    name: string;
    lat: number;
    lng: number;
    radius?: number;
  } | null>(null);
  // Computed
  const dateKey = selectedDate.toISOString().split('T')[0];
  const dayEvents = mockCalendarEvents[dateKey] || [];
  const hasEvents = dayEvents.length > 0;
  // Initialize browse items when starting in map view
  useEffect(() => {
    if (initialView === 'map' && browseItems.length === 0) {
      const opps = mockOpportunitiesByDate[dateKey] || mockAvailableItems;
      setBrowseItems(opps);
    }
  }, []);
  // Schedule Recalculation
  const scheduleAroundEvents = (items: OpportunityItem[], events: CalendarEvent[]): OpportunityItem[] => {
    if (items.length === 0) return items;
    // Sort events by time
    const sortedEvents = [...events].sort((a, b) => {
      const startA = parseTime(a.time.split(' - ')[0]);
      const startB = parseTime(b.time.split(' - ')[0]);
      return startA - startB;
    });
    let currentTime = 480; // Start at 08:00
    let currentLat = sortedEvents.length > 0 ? sortedEvents[0].lat : items[0].lat;
    let currentLng = sortedEvents.length > 0 ? sortedEvents[0].lng : items[0].lng;
    return items.map(item => {
      const duration = getDuration(item.originalTimeWindow || item.timeWindow);
      let tentativeStart = currentTime;
      let travelTime = 0;
      // Calculate travel from current location
      const dist = haversineDistance(currentLat, currentLng, item.lat, item.lng);
      travelTime = roundUpTo5(Math.ceil(dist / 80)); // 80m/min, rounded up to nearest 5
      tentativeStart += travelTime;
      // Round to nearest 5 min
      tentativeStart = Math.ceil(tentativeStart / 5) * 5;
      let tentativeEnd = tentativeStart + duration;
      // Check for conflicts with fixed events
      let conflictFound = true;
      while (conflictFound) {
        conflictFound = false;
        for (const event of sortedEvents) {
          const [eStartStr, eEndStr] = event.time.split(' - ');
          const eStart = parseTime(eStartStr);
          const eEnd = parseTime(eEndStr);
          if (tentativeStart < eEnd && tentativeEnd > eStart) {
            conflictFound = true;
            currentLat = event.lat;
            currentLng = event.lng;
            const newDist = haversineDistance(currentLat, currentLng, item.lat, item.lng);
            const newTravel = roundUpTo5(Math.ceil(newDist / 80));
            tentativeStart = eEnd + newTravel;
            tentativeStart = Math.ceil(tentativeStart / 5) * 5;
            tentativeEnd = tentativeStart + duration;
            break;
          }
        }
      }
      currentTime = tentativeEnd;
      currentLat = item.lat;
      currentLng = item.lng;
      return {
        ...item,
        timeWindow: `${formatTime(tentativeStart)} - ${formatTime(tentativeEnd)}`,
        originalTimeWindow: item.originalTimeWindow || item.timeWindow
      };
    });
  };
  // Schedule respecting explicit user ordering (used after drag-and-drop)
  // This function takes the full ordered timeline and recalculates opportunity
  // times while keeping fixed events in place, respecting the user's chosen order.
  const scheduleRespectingOrder = (orderedTimeline: TimelineEntry[], events: CalendarEvent[]): OpportunityItem[] => {
    if (orderedTimeline.length === 0) return [];
    // We walk through the timeline in order.
    // For each opportunity, we calculate its time based on:
    // - The end time of the previous entry (fixed or opportunity)
    // - If the NEXT entry is a fixed event, we schedule backwards if needed to fit before it
    const result: OpportunityItem[] = [];
    let currentTime = 480; // 08:00
    let currentLat = orderedTimeline[0].type === 'fixed' ? orderedTimeline[0].event.lat : orderedTimeline[0].item.lat;
    let currentLng = orderedTimeline[0].type === 'fixed' ? orderedTimeline[0].event.lng : orderedTimeline[0].item.lng;
    for (let i = 0; i < orderedTimeline.length; i++) {
      const entry = orderedTimeline[i];
      if (entry.type === 'fixed') {
        // Fixed event: just update currentTime and position
        const [eStartStr, eEndStr] = entry.event.time.split(' - ');
        const eEnd = parseTime(eEndStr);
        currentTime = eEnd;
        currentLat = entry.event.lat;
        currentLng = entry.event.lng;
        continue;
      }
      // Opportunity item
      const item = entry.item;
      const duration = getDuration(item.originalTimeWindow || item.timeWindow);
      // Calculate travel from current position
      const dist = haversineDistance(currentLat, currentLng, item.lat, item.lng);
      const travelTime = roundUpTo5(Math.ceil(dist / 80));
      let tentativeStart = currentTime + travelTime;
      tentativeStart = Math.ceil(tentativeStart / 5) * 5;
      let tentativeEnd = tentativeStart + duration;
      // Check if the next entry is a fixed event — if so, we need to fit before it
      const nextFixed = orderedTimeline.slice(i + 1).find(e => e.type === 'fixed');
      if (nextFixed && nextFixed.type === 'fixed') {
        const [nStartStr] = nextFixed.event.time.split(' - ');
        const nextFixedStart = parseTime(nStartStr);
        // If our tentative end would overlap with the next fixed event,
        // try to schedule earlier (before the fixed event)
        if (tentativeEnd > nextFixedStart) {
          // Schedule to end just before the next fixed event starts
          tentativeEnd = nextFixedStart;
          tentativeStart = tentativeEnd - duration;
          // Round start down to nearest 5 min
          tentativeStart = Math.floor(tentativeStart / 5) * 5;
          tentativeEnd = tentativeStart + duration;
        }
      }
      // Also check we don't overlap with any fixed event we should be BEFORE
      // (This handles the case where item is placed before a fixed event)
      let hasConflict = true;
      let iterations = 0;
      while (hasConflict && iterations < 20) {
        hasConflict = false;
        iterations++;
        for (const evt of events) {
          const [eStartStr, eEndStr] = evt.time.split(' - ');
          const eStart = parseTime(eStartStr);
          const eEnd = parseTime(eEndStr);
          if (tentativeStart < eEnd && tentativeEnd > eStart) {
            // Conflict with a fixed event
            // Check if this fixed event comes AFTER us in the user's order
            const myIndex = i;
            const fixedIndex = orderedTimeline.findIndex(e => e.type === 'fixed' && e.event.id === evt.id);
            if (fixedIndex > myIndex) {
              // We should be BEFORE this fixed event — schedule to end before it
              tentativeEnd = eStart;
              tentativeStart = tentativeEnd - duration;
              tentativeStart = Math.floor(tentativeStart / 5) * 5;
              tentativeEnd = tentativeStart + duration;
              hasConflict = false; // We resolved by moving earlier
            } else {
              // We should be AFTER this fixed event — schedule after it
              const newDist = haversineDistance(evt.lat, evt.lng, item.lat, item.lng);
              const newTravel = roundUpTo5(Math.ceil(newDist / 80));
              tentativeStart = eEnd + newTravel;
              tentativeStart = Math.ceil(tentativeStart / 5) * 5;
              tentativeEnd = tentativeStart + duration;
              hasConflict = true;
              break;
            }
          }
        }
      }
      currentTime = tentativeEnd;
      currentLat = item.lat;
      currentLng = item.lng;
      result.push({
        ...item,
        timeWindow: `${formatTime(tentativeStart)} - ${formatTime(tentativeEnd)}`,
        originalTimeWindow: item.originalTimeWindow || item.timeWindow
      });
    }
    return result;
  };
  // Handlers
  const handleConnectCalendar = () => {
    setCalendarConnected(true);
  };
  const handleDayClick = (day: number) => {
    const newDate = new Date(viewDate.getFullYear(), viewDate.getMonth(), day);
    const offsetDate = new Date(newDate.getTime() - newDate.getTimezoneOffset() * 60000);
    setSelectedDate(offsetDate);
    setStep('date-selected');
  };
  const handleConfirmAddress = (eventId: number) => {
    const event = dayEvents.find(e => e.id === eventId);
    if (event) {
      setConfirmedLocation({
        type: 'indirizzo',
        name: event.address,
        lat: event.lat,
        lng: event.lng
      });
    }
    setStep('search-prompt');
  };
  const handleManualAddressSubmit = () => {
    if (locationMode === 'zona' && selectedZone) {
      const geo = zoneGeoData[selectedZone];
      if (geo) {
        setConfirmedLocation({
          type: 'zona',
          name: selectedZone,
          lat: geo.lat,
          lng: geo.lng,
          radius: geo.radius
        });
      }
    } else if (locationMode === 'indirizzo' && inputLocation) {
      // Mock geocode: place near Prati area
      setConfirmedLocation({
        type: 'indirizzo',
        name: inputLocation,
        lat: 41.9075,
        lng: 12.466
      });
    }
    setStep('search-prompt');
  };
  // Date navigation handler for bottom chevrons
  const handleDateNav = (direction: 'prev' | 'next') => {
    const newDate = new Date(selectedDate);
    if (direction === 'next') {
      newDate.setDate(newDate.getDate() + 1);
    } else {
      newDate.setDate(newDate.getDate() - 1);
    }
    const offsetDate = new Date(newDate.getTime() - newDate.getTimezoneOffset() * 60000);
    setSelectedDate(offsetDate);
    // Load opportunities for the new date
    const newDateKey = offsetDate.toISOString().split('T')[0];
    const opps = mockOpportunitiesByDate[newDateKey] || mockAvailableItems;
    setBrowseItems(opps);
    setBrowseSearchQuery('');
    // Reset group state
    setPackages([]);
    setSelectedGroupIndex(0);
    setCustomGroupItems([]);
    setAvailableItems([]);
    setShowDeleteConfirm(false);
    // Go to browse view
    setStep('browse-opportunities');
    // Update map bounds for all opportunities
    const allItems = opps;
    const newEvents = mockCalendarEvents[newDateKey] || [];
    const lats = allItems.map(i => i.lat);
    const lngs = allItems.map(i => i.lng);
    newEvents.forEach(e => {
      lats.push(e.lat);
      lngs.push(e.lng);
    });
    if (lats.length > 0) {
      setMapBounds([[Math.min(...lats), Math.min(...lngs)], [Math.max(...lats), Math.max(...lngs)]]);
    }
  };
  // AI grouping from browse view
  const handleAiGroupFromBrowse = () => {
    setStep('loading');
    setTimeout(() => {
      // Create AI-generated groups from browse items
      const items = [...browseItems];
      const groupSize = Math.ceil(items.length / 3);
      const aiGroups: OpportunityGroup[] = [];
      for (let i = 0; i < items.length; i += groupSize) {
        const groupItems = items.slice(i, i + groupSize);
        const scheduled = scheduleAroundEvents(groupItems, dayEvents);
        aiGroups.push({
          id: aiGroups.length + 1,
          items: scheduled
        });
      }
      if (aiGroups.length === 0) {
        aiGroups.push({
          id: 1,
          items: []
        });
      }
      setPackages(aiGroups);
      setSelectedGroupIndex(0);
      // Set remaining items as available for compose mode
      setAvailableItems([]);
      setStep('results');
      setShowAiModal(true);
      if (aiGroups[0]) {
        updateMapBounds(aiGroups[0].items);
      }
    }, 2500);
  };
  // Manual group creation from browse view
  const handleManualGroupFromBrowse = () => {
    setCustomGroupItems([]);
    setAvailableItems([...browseItems]);
    setStep('custom-compose');
  };
  // Add single item to new group from browse
  const handleAddFromBrowse = (item: OpportunityItem) => {
    const remaining = browseItems.filter(i => i.id !== item.id);
    setCustomGroupItems([{
      ...item,
      originalTimeWindow: item.timeWindow
    }]);
    setAvailableItems(remaining);
    const scheduled = scheduleAroundEvents([{
      ...item,
      originalTimeWindow: item.timeWindow
    }], dayEvents);
    setCustomGroupItems(scheduled);
    setStep('custom-compose');
  };
  const handleStartSearch = () => {
    setStep('loading');
    onViewChange?.('map');
    setTimeout(() => {
      setStep('results');
      setShowAiModal(true);
      // Initial calculation for the first group
      const firstGroup = packages[0];
      if (firstGroup) {
        const scheduled = scheduleAroundEvents(firstGroup.items, dayEvents);
        const newPackages = [...packages];
        newPackages[0] = {
          ...firstGroup,
          items: scheduled
        };
        setPackages(newPackages);
        updateMapBounds(scheduled);
      }
    }, 2500);
  };
  const handleStartCompose = () => {
    // Pre-populate with current group items (deep copy)
    const currentItems = packages[selectedGroupIndex]?.items.map(i => ({
      ...i
    })) || [];
    setCustomGroupItems(currentItems);
    // If we came from browse, use browseItems minus current group items as available
    if (browseItems.length > 0) {
      const currentIds = new Set(currentItems.map(i => i.id));
      const remaining = browseItems.filter(i => !currentIds.has(i.id));
      setAvailableItems(remaining);
    }
    setStep('custom-compose');
  };
  const handleAddToGroup = (item: OpportunityItem) => {
    const newItem = {
      ...item,
      originalTimeWindow: item.timeWindow
    };
    // Build current timeline preserving user's ordering
    const currentTimeline = getMergedTimeline(customGroupItems);
    // Append new item at the end of the timeline
    currentTimeline.push({
      type: 'opportunity' as const,
      item: newItem,
      letter: ''
    });
    // Recalculate using order-respecting scheduler to preserve existing order
    const recalculated = scheduleRespectingOrder(currentTimeline, dayEvents);
    setCustomGroupItems(recalculated);
    setAvailableItems(availableItems.filter(i => i.id !== item.id));
  };
  const handleRemoveFromGroup = (item: OpportunityItem) => {
    const newItems = customGroupItems.filter(i => i.id !== item.id);
    const recalculatedItems = scheduleAroundEvents(newItems, dayEvents);
    setCustomGroupItems(recalculatedItems);
    // Reset item time window before adding back to available
    const originalItem = {
      ...item,
      timeWindow: item.originalTimeWindow || item.timeWindow
    };
    setAvailableItems([...availableItems, originalItem]);
  };
  const handleConfirmGroup = () => {
    const hasDistantItems = customGroupItems.some(item => item.address.includes('Ottaviano') || item.address.includes('Candia'));
    if (hasDistantItems && !showDistanceWarning) {
      setShowDistanceWarning(true);
      return;
    }
    const newGroup: OpportunityGroup = {
      id: packages.length + 1,
      items: [...customGroupItems],
      isCustom: true
    };
    setPackages([...packages, newGroup]);
    setSelectedGroupIndex(packages.length);
    setStep('results');
    setShowDistanceWarning(false);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };
  const handleDeleteGroup = () => {
    if (packages.length <= 1) return; // Prevent deleting last group
    if (!showDeleteConfirm) {
      setShowDeleteConfirm(true);
      return;
    }
    const newPackages = packages.filter((_, i) => i !== selectedGroupIndex);
    setPackages(newPackages);
    setSelectedGroupIndex(0);
    setShowDeleteConfirm(false);
  };
  // Handler to open map from calendar slot
  const handleOpenMapFromSlot = (dayDate: Date) => {
    setSelectedDate(dayDate);
    const offsetDate = new Date(dayDate.getTime() - dayDate.getTimezoneOffset() * 60000);
    const newDateKey = offsetDate.toISOString().split('T')[0];
    const opps = mockOpportunitiesByDate[newDateKey] || mockAvailableItems;
    setBrowseItems(opps);
    setBrowseSearchQuery('');
    setPackages([]);
    setSelectedGroupIndex(0);
    setCustomGroupItems([]);
    setAvailableItems([]);
    setShowDeleteConfirm(false);
    setStep('browse-opportunities');
    onViewChange?.('map');
    // Update map bounds
    const newEvents = mockCalendarEvents[newDateKey] || [];
    const lats = opps.map(i => i.lat);
    const lngs = opps.map(i => i.lng);
    newEvents.forEach(e => {
      lats.push(e.lat);
      lngs.push(e.lng);
    });
    if (lats.length > 0) {
      setMapBounds([[Math.min(...lats), Math.min(...lngs)], [Math.max(...lats), Math.max(...lngs)]]);
    }
    setSlotPopover(null);
  };
  // Drag and Drop Handlers
  const [draggedItemId, setDraggedItemId] = useState<number | null>(null);
  const [dropTargetIndex, setDropTargetIndex] = useState<number | null>(null);
  const handleTimelineDragStart = (e: React.DragEvent, itemId: number) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(itemId));
    setDraggedItemId(itemId);
  };
  const handleTimelineDragOver = (e: React.DragEvent, timelineIdx: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (draggedItemId === null) return;
    setDropTargetIndex(timelineIdx);
  };
  const handleTimelineDrop = (e: React.DragEvent, timelineIdx: number) => {
    e.preventDefault();
    if (draggedItemId === null) return;
    // Build the current merged timeline (with current scheduled times)
    const currentTimeline = getMergedTimeline(customGroupItems);
    // Find the dragged item
    const draggedItem = customGroupItems.find(i => i.id === draggedItemId);
    if (!draggedItem) return;
    // Remove the dragged item's entry from timeline
    const timelineWithout = currentTimeline.filter(entry => !(entry.type === 'opportunity' && entry.item.id === draggedItemId));
    // Insert at the target position (clamped to valid range)
    const insertAt = Math.min(timelineIdx, timelineWithout.length);
    const newTimeline: TimelineEntry[] = [...timelineWithout.slice(0, insertAt), {
      type: 'opportunity' as const,
      item: draggedItem,
      letter: ''
    }, ...timelineWithout.slice(insertAt)];
    // Use the order-respecting scheduler that handles positions relative to fixed events
    const recalculated = scheduleRespectingOrder(newTimeline, dayEvents);
    setCustomGroupItems(recalculated);
    setDraggedItemId(null);
    setDropTargetIndex(null);
  };
  const handleTimelineDragEnd = () => {
    setDraggedItemId(null);
    setDropTargetIndex(null);
  };
  const updateMapBounds = (items: OpportunityItem[]) => {
    if (items.length === 0 && dayEvents.length === 0) return;
    const lats = items.map(i => i.lat);
    const lngs = items.map(i => i.lng);
    dayEvents.forEach(e => {
      lats.push(e.lat);
      lngs.push(e.lng);
    });
    if (lats.length === 0) return;
    setMapBounds([[Math.min(...lats), Math.min(...lngs)], [Math.max(...lats), Math.max(...lngs)]]);
  };
  useEffect(() => {
    if (step === 'results' && packages[selectedGroupIndex]) {
      // Recalculate when switching groups
      const group = packages[selectedGroupIndex];
      const scheduled = scheduleAroundEvents(group.items, dayEvents);
      // We don't want to mutate state in render, but we need to display scheduled times
      // Ideally we'd store scheduled state separately or update packages
      // For now, let's just update bounds
      updateMapBounds(group.items);
    }
  }, [selectedGroupIndex, step]);
  useEffect(() => {
    if (step === 'custom-compose') {
      updateMapBounds(customGroupItems);
    }
  }, [customGroupItems, step]);
  useEffect(() => {
    if (step === 'browse-opportunities') {
      const lats = browseItems.map(i => i.lat);
      const lngs = browseItems.map(i => i.lng);
      dayEvents.forEach(e => {
        lats.push(e.lat);
        lngs.push(e.lng);
      });
      if (lats.length > 0) {
        setMapBounds([[Math.min(...lats), Math.min(...lngs)], [Math.max(...lats), Math.max(...lngs)]]);
      }
    }
  }, [browseItems, step]);
  // Helper to merge and sort timeline
  const getMergedTimeline = (items: OpportunityItem[]): TimelineEntry[] => {
    const timeline: TimelineEntry[] = [];
    dayEvents.forEach(event => {
      timeline.push({
        type: 'fixed',
        event
      });
    });
    items.forEach((item, index) => {
      timeline.push({
        type: 'opportunity',
        item,
        letter: String.fromCharCode(65 + index)
      });
    });
    return timeline.sort((a, b) => {
      const timeA = a.type === 'fixed' ? a.event.time : a.item.timeWindow;
      const timeB = b.type === 'fixed' ? b.event.time : b.item.timeWindow;
      const startA = parseTime(timeA.split(' - ')[0]);
      const startB = parseTime(timeB.split(' - ')[0]);
      return startA - startB;
    });
  };
  // Render Helpers
  const renderWeeklyCalendar = () => {
    const weekDays = getWeekDays(weekStartDate);
    const startHour = 8; // 08:00
    const endHour = 20; // 20:00
    const totalSlots = endHour - startHour; // 1h slots (12 slots)
    // Generate time labels - one per hour
    const timeLabels: string[] = [];
    for (let i = 0; i < totalSlots; i++) {
      const minutes = startHour * 60 + i * 60;
      timeLabels.push(formatTime(minutes));
    }
    return <div className="bg-white rounded-xl border border-gray-200 shadow-sm w-full overflow-hidden flex flex-col h-full" data-id="element-1756">
        {/* Header - Outlook style */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-white" data-id="element-1757">
          <button onClick={() => {
          const newDate = new Date(weekStartDate);
          newDate.setDate(newDate.getDate() - 7);
          setWeekStartDate(newDate);
        }} className="p-1.5 hover:bg-gray-100 rounded-md text-gray-500 transition-colors" data-id="element-1758">
            <ChevronLeft size={18} data-id="element-1759" />
          </button>
          <span className="text-sm font-semibold text-navy" data-id="element-1760">
            {weekStartDate.getDate()} {months[weekStartDate.getMonth()]} –{' '}
            {weekDays[6].getDate()} {months[weekDays[6].getMonth()]}{' '}
            {weekDays[6].getFullYear()}
          </span>
          <button onClick={() => {
          const newDate = new Date(weekStartDate);
          newDate.setDate(newDate.getDate() + 7);
          setWeekStartDate(newDate);
        }} className="p-1.5 hover:bg-gray-100 rounded-md text-gray-500 transition-colors" data-id="element-1761">
            <ChevronRight size={18} data-id="element-1762" />
          </button>
        </div>

        {/* Calendar Grid */}
        <div className="flex flex-1 overflow-hidden" data-id="element-1763">
          {/* Time Column */}
          <div className="w-14 flex-shrink-0 border-r border-gray-200 bg-white" data-id="element-1764">
            {/* Spacer for day headers */}
            <div className="h-[52px] border-b border-gray-200" data-id="element-1765"></div>
            {timeLabels.map((label, i) => <div key={i} className="h-[60px] flex items-start justify-end pr-2 -mt-[7px]" data-id="element-1766">
                <span className="text-[11px] text-gray-400 font-medium" data-id="element-1767">
                  {label}
                </span>
              </div>)}
          </div>

          {/* Days Columns */}
          <div className="flex-1 overflow-y-auto" data-id="element-1768">
            <div className="grid grid-cols-7 min-w-[700px]" data-id="element-1769">
              {weekDays.map((day, dayIndex) => {
              const isToday = isSameDay(day, new Date(2026, 1, 10)); // Mock today
              const dateKey = day.toISOString().split('T')[0];
              const dayEventsForCol = calendarConnected ? mockCalendarEvents[dateKey] || [] : [];
              return <div key={dayIndex} className={`border-r border-gray-100 last:border-r-0 flex flex-col`} data-id="element-1770">
                    {/* Day Header - Outlook style */}
                    <div className={`sticky top-0 z-10 border-b border-gray-200 py-2 px-1 text-center ${isToday ? 'bg-teal-50/40' : 'bg-white'}`} data-id="element-1771">
                      <p className="text-[11px] text-gray-400 uppercase font-medium" data-id="element-1772">
                        {['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'][day.getDay() === 0 ? 6 : day.getDay() - 1]}
                      </p>
                      <div className={`w-7 h-7 mx-auto flex items-center justify-center rounded-full text-sm font-bold mt-0.5 ${isToday ? 'bg-teal-deep text-white' : 'text-navy'}`} data-id="element-1773">
                        {day.getDate()}
                      </div>
                    </div>

                    {/* Slots - 1h each */}
                    <div className="relative" style={{
                  height: `${totalSlots * 60}px`
                }} data-id="element-1774">
                      {Array.from({
                    length: totalSlots
                  }).map((_, slotIndex) => {
                    const slotStartMinutes = startHour * 60 + slotIndex * 60;
                    const slotEndMinutes = slotStartMinutes + 60;
                    // Check for events starting in this slot
                    const eventStartingHere = dayEventsForCol.find(e => {
                      const start = parseTime(e.time.split(' - ')[0]);
                      return start >= slotStartMinutes && start < slotEndMinutes;
                    });
                    // Check if slot is occupied by an event starting earlier
                    const isOccupied = dayEventsForCol.some(e => {
                      const start = parseTime(e.time.split(' - ')[0]);
                      const end = parseTime(e.time.split(' - ')[1]);
                      return slotStartMinutes >= start && slotEndMinutes <= end && start < slotStartMinutes;
                    });
                    if (isOccupied) return null;
                    if (eventStartingHere) {
                      const start = parseTime(eventStartingHere.time.split(' - ')[0]);
                      const end = parseTime(eventStartingHere.time.split(' - ')[1]);
                      const durationMinutes = end - start;
                      const heightPx = durationMinutes / 60 * 60 - 4;
                      const topPx = (start - startHour * 60) / 60 * 60 + 2;
                      return <div key={`event-${eventStartingHere.id}`} className="absolute w-[calc(100%-6px)] left-[3px] z-10 rounded-md border-l-[3px] border-teal-deep bg-teal-deep/8 p-1.5 overflow-hidden cursor-pointer hover:bg-teal-deep/12 transition-colors" style={{
                        top: `${topPx}px`,
                        height: `${heightPx}px`
                      }} data-id="element-1775">
                              <span className="text-[10px] font-semibold text-navy block truncate" data-id="element-1776">
                                {eventStartingHere.time}
                              </span>
                              <p className="text-[11px] font-bold text-navy leading-tight truncate mt-0.5" data-id="element-1777">
                                {eventStartingHere.address}
                              </p>
                              <p className="text-[10px] text-gray-500 truncate" data-id="element-1778">
                                {eventStartingHere.type}
                              </p>
                            </div>;
                    }
                    // Free Slot - Outlook style with subtle hour line
                    return <div key={`slot-${slotIndex}`} className="absolute w-full border-b border-gray-100 hover:bg-teal-50/20 transition-colors group cursor-pointer" style={{
                      top: `${slotIndex * 60}px`,
                      height: '60px'
                    }} onClick={e => {
                      const rect = e.currentTarget.getBoundingClientRect();
                      setSlotPopover({
                        dayDate: day,
                        startMinutes: slotStartMinutes,
                        endMinutes: slotEndMinutes,
                        anchorRect: rect
                      });
                    }} data-id="element-1779">
                            <div className="hidden group-hover:flex w-full h-full items-center justify-center" data-id="element-1780">
                              <Plus size={16} className="text-teal-deep opacity-40" data-id="element-1781" />
                            </div>
                          </div>;
                  })}
                    </div>
                  </div>;
            })}
            </div>
          </div>
        </div>
      </div>;
  };
  // --- Views ---
  if (step === 'calendar-setup' || step === 'date-selected' || step === 'search-prompt') {
    return <div className="h-full w-full bg-gray-50 flex flex-col p-4 lg:p-8 relative" data-id="element-1782">
        <div className="w-full flex flex-col flex-1 space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-1783">
          {/* Header row with title left, calendar buttons + CTA right */}
          <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4" data-id="element-1784">
            <div data-id="element-1785">
              <h1 className="text-xl lg:text-2xl font-bold font-montserrat text-navy mb-1" data-id="element-1786">
                Pianificatore
              </h1>
              <p className="text-xs lg:text-sm text-gray-500" data-id="element-1787">
                {step === 'calendar-setup' ? 'Seleziona uno slot libero per iniziare' : 'Gestisci i tuoi interventi e trova nuove opportunità'}
              </p>
            </div>

            {/* Right side: calendar connectors + primary CTA */}
            <div className="flex items-center gap-2 flex-shrink-0" data-id="element-1788">
              {/* Google Calendar */}
              {connectedCalendars.google ? <div className="flex items-center gap-2 bg-green-50 border border-green-200 text-green-700 rounded-lg font-medium text-xs px-3 py-2 min-h-[36px]" data-id="element-1789">
                  <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/a/a5/Google_Calendar_icon_%282020%29.svg/500px-Google_Calendar_icon_%282020%29.svg.png" alt="Google Calendar" className="w-4 h-4 object-contain" data-id="element-1790" />
                  <CheckCircle size={12} data-id="element-1791" />
                  <span data-id="element-1792">Connesso</span>
                </div> : <button onClick={() => setShowGoogleAuthModal(true)} className="flex items-center gap-2 bg-white border border-gray-200 text-navy rounded-lg hover:bg-gray-50 font-medium text-xs px-4 py-2 transition-colors min-h-[36px]" data-id="element-1793">
                  <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/a/a5/Google_Calendar_icon_%282020%29.svg/500px-Google_Calendar_icon_%282020%29.svg.png" alt="Google Calendar" className="w-4 h-4 object-contain flex-shrink-0" data-id="element-1794" />
                  <span data-id="element-1795">Google Calendar</span>
                </button>}

              {/* Outlook */}
              {connectedCalendars.outlook ? <div className="flex items-center gap-2 bg-green-50 border border-green-200 text-green-700 rounded-lg font-medium text-xs px-3 py-2 min-h-[36px]" data-id="element-1796">
                  <img src="https://img.icons8.com/color/600/outlook-calendar.png" alt="Outlook" className="w-4 h-4 object-contain" data-id="element-1797" />
                  <CheckCircle size={12} data-id="element-1798" />
                  <span data-id="element-1799">Connesso</span>
                </div> : <button onClick={() => setShowOutlookAuthModal(true)} className="flex items-center gap-2 bg-white border border-gray-200 text-navy rounded-lg hover:bg-gray-50 font-medium text-xs px-4 py-2 transition-colors min-h-[36px]" data-id="element-1800">
                  <img src="https://img.icons8.com/color/600/outlook-calendar.png" alt="Outlook" className="w-4 h-4 object-contain flex-shrink-0" data-id="element-1801" />
                  <span data-id="element-1802">Outlook</span>
                </button>}

              {/* Primary CTA */}
              <button onClick={() => handleOpenMapFromSlot(new Date(2026, 1, 10))} className="flex items-center gap-2 bg-teal-deep text-white rounded-lg hover:bg-teal-deep/90 font-medium text-sm px-5 py-2 transition-colors shadow-sm min-h-[36px] whitespace-nowrap" data-id="element-1803">
                <Search size={16} data-id="element-1804" />
                Cerca intervento
              </button>
            </div>
          </div>

          <div className="flex-1 min-h-0" data-id="element-1805">{renderWeeklyCalendar()}</div>

          {step === 'calendar-setup' && <>
              {/* Show events for selected date when calendar is connected */}
              {calendarConnected && hasEvents && <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm animate-in fade-in slide-in-from-bottom-2" data-id="element-1806">
                  <h3 className="font-semibold text-navy mb-4 flex items-center gap-2" data-id="element-1807">
                    <CalendarIcon size={16} className="text-teal-deep" data-id="element-1808" />
                    Interventi del {selectedDate.getDate()}{' '}
                    {months[selectedDate.getMonth()]}
                  </h3>
                  <div className="space-y-3" data-id="element-1809">
                    {dayEvents.map((event, index) => <div key={event.id} className="border border-gray-100 rounded-lg p-4 bg-gray-50/50 flex flex-col gap-3" data-id="element-1810">
                        <div className="flex justify-between items-start" data-id="element-1811">
                          <div className="flex items-start gap-2.5" data-id="element-1812">
                            {event.source === 'google' ? <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/a/a5/Google_Calendar_icon_%282020%29.svg/500px-Google_Calendar_icon_%282020%29.svg.png" alt="Google" className="w-5 h-5 object-contain mt-0.5 flex-shrink-0" data-id="element-1813" /> : event.source === 'outlook' ? <img src="https://img.icons8.com/color/600/outlook-calendar.png" alt="Outlook" className="w-5 h-5 object-contain mt-0.5 flex-shrink-0" data-id="element-1814" /> : <CalendarIcon size={18} className="text-gray-400 mt-0.5 flex-shrink-0" data-id="element-1815" />}
                            <div data-id="element-1816">
                              <p className="font-bold text-navy text-sm" data-id="element-1817">
                                {event.address}
                              </p>
                              <p className="text-xs text-gray-500 mt-0.5" data-id="element-1818">
                                {event.time} • {event.type}
                              </p>
                            </div>
                          </div>
                          <div className="w-8 h-8 rounded-full bg-navy/10 flex items-center justify-center text-navy font-bold text-xs" data-id="element-1819">
                            {index + 1}
                          </div>
                        </div>
                        <button onClick={() => handleConfirmAddress(event.id)} className="w-full mt-2 bg-white border border-gray-200 text-navy text-xs font-medium py-2 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-1820">
                          Conferma indirizzo
                        </button>
                      </div>)}
                  </div>
                </div>}

              {/* Google Auth Confirmation Modal */}
              {showGoogleAuthModal && <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[500] flex items-center justify-center p-4 animate-in fade-in duration-200" data-id="element-1821">
                  <div className="bg-white rounded-xl shadow-2xl max-w-sm w-full overflow-hidden animate-in zoom-in-95 duration-200" data-id="element-1822">
                    <div className="p-6 pb-4 flex items-start justify-between" data-id="element-1823">
                      <div className="flex items-center gap-3" data-id="element-1824">
                        <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/a/a5/Google_Calendar_icon_%282020%29.svg/500px-Google_Calendar_icon_%282020%29.svg.png" alt="Google Calendar" className="w-10 h-10 object-contain" data-id="element-1825" />
                        <div data-id="element-1826">
                          <h3 className="font-bold text-navy text-base" data-id="element-1827">
                            Google Calendar
                          </h3>
                          <p className="text-xs text-gray-500" data-id="element-1828">
                            calendar.google.com
                          </p>
                        </div>
                      </div>
                      <button onClick={() => setShowGoogleAuthModal(false)} className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-1829">
                        <X size={18} className="text-gray-400" data-id="element-1830" />
                      </button>
                    </div>
                    <div className="px-6 pb-4" data-id="element-1831">
                      <p className="text-sm text-gray-600 mb-4" data-id="element-1832">
                        CleanPlanner vuole accedere al tuo Google Calendar per
                        sincronizzare i tuoi interventi.
                      </p>
                      <div className="bg-gray-50 rounded-lg p-4 space-y-3 mb-4" data-id="element-1833">
                        <p className="text-xs font-semibold text-navy uppercase tracking-wider" data-id="element-1834">
                          Permessi richiesti
                        </p>
                        <div className="flex items-center gap-3" data-id="element-1835">
                          <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0" data-id="element-1836">
                            <CalendarIcon size={14} className="text-blue-600" data-id="element-1837" />
                          </div>
                          <div data-id="element-1838">
                            <p className="text-sm font-medium text-navy" data-id="element-1839">
                              Visualizzare i tuoi eventi
                            </p>
                            <p className="text-xs text-gray-500" data-id="element-1840">
                              Accesso in sola lettura al calendario
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3" data-id="element-1841">
                          <div className="w-8 h-8 rounded-full bg-green-50 flex items-center justify-center flex-shrink-0" data-id="element-1842">
                            <Check size={14} className="text-green-600" data-id="element-1843" />
                          </div>
                          <div data-id="element-1844">
                            <p className="text-sm font-medium text-navy" data-id="element-1845">
                              Sincronizzare gli interventi
                            </p>
                            <p className="text-xs text-gray-500" data-id="element-1846">
                              Aggiungere interventi confermati
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="px-6 pb-6 flex gap-3" data-id="element-1847">
                      <button onClick={() => setShowGoogleAuthModal(false)} className="flex-1 py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-50 rounded-lg border border-gray-200 transition-colors" data-id="element-1848">
                        Annulla
                      </button>
                      <button onClick={() => {
                  setConnectedCalendars(prev => ({
                    ...prev,
                    google: true
                  }));
                  setShowGoogleAuthModal(false);
                }} className="flex-1 py-2.5 text-sm font-medium bg-teal-deep text-white rounded-lg hover:bg-teal-deep/90 shadow-sm transition-colors" data-id="element-1849">
                        Consenti accesso
                      </button>
                    </div>
                  </div>
                </div>}

              {/* Outlook Auth Confirmation Modal */}
              {showOutlookAuthModal && <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[500] flex items-center justify-center p-4 animate-in fade-in duration-200" data-id="element-1850">
                  <div className="bg-white rounded-xl shadow-2xl max-w-sm w-full overflow-hidden animate-in zoom-in-95 duration-200" data-id="element-1851">
                    <div className="p-6 pb-4 flex items-start justify-between" data-id="element-1852">
                      <div className="flex items-center gap-3" data-id="element-1853">
                        <img src="https://img.icons8.com/color/600/outlook-calendar.png" alt="Outlook" className="w-10 h-10 object-contain" data-id="element-1854" />
                        <div data-id="element-1855">
                          <h3 className="font-bold text-navy text-base" data-id="element-1856">
                            Outlook Calendar
                          </h3>
                          <p className="text-xs text-gray-500" data-id="element-1857">
                            outlook.office.com
                          </p>
                        </div>
                      </div>
                      <button onClick={() => setShowOutlookAuthModal(false)} className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-1858">
                        <X size={18} className="text-gray-400" data-id="element-1859" />
                      </button>
                    </div>
                    <div className="px-6 pb-4" data-id="element-1860">
                      <p className="text-sm text-gray-600 mb-4" data-id="element-1861">
                        CleanPlanner vuole accedere al tuo Outlook Calendar per
                        sincronizzare i tuoi interventi.
                      </p>
                      <div className="bg-gray-50 rounded-lg p-4 space-y-3 mb-4" data-id="element-1862">
                        <p className="text-xs font-semibold text-navy uppercase tracking-wider" data-id="element-1863">
                          Permessi richiesti
                        </p>
                        <div className="flex items-center gap-3" data-id="element-1864">
                          <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0" data-id="element-1865">
                            <CalendarIcon size={14} className="text-blue-600" data-id="element-1866" />
                          </div>
                          <div data-id="element-1867">
                            <p className="text-sm font-medium text-navy" data-id="element-1868">
                              Visualizzare i tuoi eventi
                            </p>
                            <p className="text-xs text-gray-500" data-id="element-1869">
                              Accesso in sola lettura al calendario
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3" data-id="element-1870">
                          <div className="w-8 h-8 rounded-full bg-green-50 flex items-center justify-center flex-shrink-0" data-id="element-1871">
                            <Check size={14} className="text-green-600" data-id="element-1872" />
                          </div>
                          <div data-id="element-1873">
                            <p className="text-sm font-medium text-navy" data-id="element-1874">
                              Sincronizzare gli interventi
                            </p>
                            <p className="text-xs text-gray-500" data-id="element-1875">
                              Aggiungere interventi confermati
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="px-6 pb-6 flex gap-3" data-id="element-1876">
                      <button onClick={() => setShowOutlookAuthModal(false)} className="flex-1 py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-50 rounded-lg border border-gray-200 transition-colors" data-id="element-1877">
                        Annulla
                      </button>
                      <button onClick={() => {
                  setConnectedCalendars(prev => ({
                    ...prev,
                    outlook: true
                  }));
                  setShowOutlookAuthModal(false);
                }} className="flex-1 py-2.5 text-sm font-medium bg-[#0078D4] text-white rounded-lg hover:bg-[#0078D4]/90 shadow-sm transition-colors" data-id="element-1878">
                        Consenti accesso
                      </button>
                    </div>
                  </div>
                </div>}
            </>}

          {step === 'date-selected' && <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm animate-in fade-in slide-in-from-bottom-2" data-id="element-1879">
              <h3 className="font-semibold text-navy mb-4 flex items-center gap-2" data-id="element-1880">
                <CalendarIcon size={16} className="text-teal-deep" data-id="element-1881" />
                Interventi del {selectedDate.getDate()}{' '}
                {months[selectedDate.getMonth()]}
              </h3>

              {hasEvents ? <div className="space-y-3" data-id="element-1882">
                  {dayEvents.map((event, index) => <div key={event.id} className="border border-gray-100 rounded-lg p-4 bg-gray-50/50 flex flex-col gap-3" data-id="element-1883">
                      <div className="flex justify-between items-start" data-id="element-1884">
                        <div className="flex items-start gap-2.5" data-id="element-1885">
                          {event.source === 'google' ? <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/a/a5/Google_Calendar_icon_%282020%29.svg/500px-Google_Calendar_icon_%282020%29.svg.png" alt="Google" className="w-5 h-5 object-contain mt-0.5 flex-shrink-0" data-id="element-1886" /> : event.source === 'outlook' ? <img src="https://img.icons8.com/color/600/outlook-calendar.png" alt="Outlook" className="w-5 h-5 object-contain mt-0.5 flex-shrink-0" data-id="element-1887" /> : <CalendarIcon size={18} className="text-gray-400 mt-0.5 flex-shrink-0" data-id="element-1888" />}
                          <div data-id="element-1889">
                            <p className="font-bold text-navy text-sm" data-id="element-1890">
                              {event.address}
                            </p>
                            <p className="text-xs text-gray-500 mt-0.5" data-id="element-1891">
                              {event.time} • {event.type}
                            </p>
                          </div>
                        </div>
                        <div className="w-8 h-8 rounded-full bg-navy/10 flex items-center justify-center text-navy font-bold text-xs" data-id="element-1892">
                          {index + 1}
                        </div>
                      </div>
                      <button onClick={() => handleConfirmAddress(event.id)} className="w-full mt-2 bg-white border border-gray-200 text-navy text-xs font-medium py-2 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-1893">
                        Conferma indirizzo
                      </button>
                    </div>)}
                </div> : <div className="space-y-4" data-id="element-1894">
                  {/* Location Mode Switcher */}
                  <div className="flex p-1 bg-gray-100 rounded-lg" data-id="element-1895">
                    <button onClick={() => setLocationMode('zona')} className={`flex-1 py-1.5 text-xs font-medium rounded-md transition-all ${locationMode === 'zona' ? 'bg-white text-navy shadow-sm' : 'text-gray-500 hover:text-navy'}`} data-id="element-1896">
                      Zona
                    </button>
                    <button onClick={() => setLocationMode('indirizzo')} className={`flex-1 py-1.5 text-xs font-medium rounded-md transition-all ${locationMode === 'indirizzo' ? 'bg-white text-navy shadow-sm' : 'text-gray-500 hover:text-navy'}`} data-id="element-1897">
                      Indirizzo
                    </button>
                  </div>

                  {locationMode === 'indirizzo' ? <div className="relative" data-id="element-1898">
                      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" data-id="element-1899" />
                      <input type="text" value={inputLocation} onChange={e => setInputLocation(e.target.value)} placeholder="Inserisci un indirizzo di partenza..." className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-deep focus:border-transparent outline-none" data-id="element-1900" />
                    </div> : <div className="flex flex-wrap gap-2" data-id="element-1901">
                      {['Prati', 'Centro Storico', 'Testaccio', 'Trastevere'].map(zone => <button key={zone} onClick={() => setSelectedZone(zone)} className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors border ${selectedZone === zone ? 'bg-teal-50 text-teal-deep border-teal-200' : 'bg-gray-50 text-gray-600 border-transparent hover:bg-gray-100'}`} data-id="element-1902">
                          {zone}
                        </button>)}
                    </div>}

                  <button onClick={handleManualAddressSubmit} disabled={locationMode === 'indirizzo' ? !inputLocation : !selectedZone} className="w-full bg-teal-deep text-white rounded-lg hover:bg-teal-deep/90 font-medium text-sm py-2.5 shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-1903">
                    Conferma e Cerca
                  </button>
                </div>}
            </div>}

          {step === 'search-prompt' && <div className="bg-gradient-to-br from-teal-50 to-white border border-teal-100 rounded-xl p-6 shadow-md text-center animate-in zoom-in-95 duration-300" data-id="element-1904">
              <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-3 text-teal-deep" data-id="element-1905">
                <Sparkles size={24} data-id="element-1906" />
              </div>
              <h3 className="font-bold text-navy mb-2" data-id="element-1907">
                Vuoi cercare opportunità vicine?
              </h3>
              <p className="text-sm text-gray-600 mb-6" data-id="element-1908">
                CleanAI può trovare interventi compatibili vicino ai tuoi
                appuntamenti.
              </p>
              <div className="flex gap-3" data-id="element-1909">
                <button onClick={() => setStep('date-selected')} className="flex-1 py-2.5 text-sm font-medium text-gray-500 hover:text-navy hover:bg-gray-50 rounded-lg transition-colors" data-id="element-1910">
                  No, grazie
                </button>
                <button onClick={handleStartSearch} className="flex-1 py-2.5 text-sm font-medium bg-teal-deep text-white rounded-lg hover:bg-teal-deep/90 shadow-sm transition-colors" data-id="element-1911">
                  Sì, cerca opportunità
                </button>
              </div>
            </div>}
        </div>

        {/* Slot Popover */}
        {slotPopover && <>
            <div className="fixed inset-0 z-[100]" onClick={() => setSlotPopover(null)} data-id="element-1912"></div>
            <div className="fixed z-[101] bg-white rounded-xl shadow-xl border border-gray-200 p-4 w-72 animate-in zoom-in-95 duration-200" style={{
          top: slotPopover.anchorRect ? Math.min(window.innerHeight - 200, slotPopover.anchorRect.bottom + 10) : '50%',
          left: slotPopover.anchorRect ? Math.min(window.innerWidth - 300, Math.max(10, slotPopover.anchorRect.left - 100)) : '50%',
          transform: slotPopover.anchorRect ? 'none' : 'translate(-50%, -50%)'
        }} data-id="element-1913">
              <div className="flex justify-between items-start mb-3" data-id="element-1914">
                <div data-id="element-1915">
                  <h4 className="font-bold text-navy text-sm" data-id="element-1916">
                    {slotPopover.dayDate.getDate()}{' '}
                    {months[slotPopover.dayDate.getMonth()]}
                  </h4>
                  <p className="text-xs text-gray-500" data-id="element-1917">
                    {formatTime(slotPopover.startMinutes)} -{' '}
                    {formatTime(slotPopover.endMinutes)}
                  </p>
                </div>
                <button onClick={() => setSlotPopover(null)} className="text-gray-400 hover:text-gray-600" data-id="element-1918">
                  <X size={16} data-id="element-1919" />
                </button>
              </div>

              <div className="space-y-2" data-id="element-1920">
                <button onClick={() => handleOpenMapFromSlot(slotPopover.dayDate)} className="w-full flex items-center gap-2 p-2 hover:bg-teal-50 rounded-lg text-left transition-colors group" data-id="element-1921">
                  <div className="w-8 h-8 rounded-full bg-teal-100 text-teal-deep flex items-center justify-center group-hover:bg-teal-deep group-hover:text-white transition-colors" data-id="element-1922">
                    <Clock size={14} data-id="element-1923" />
                  </div>
                  <div data-id="element-1924">
                    <p className="text-xs font-bold text-navy" data-id="element-1925">
                      Cerca per questo slot
                    </p>
                    <p className="text-[10px] text-gray-500" data-id="element-1926">
                      Solo {formatTime(slotPopover.startMinutes)} -{' '}
                      {formatTime(slotPopover.endMinutes)}
                    </p>
                  </div>
                </button>

                <button onClick={() => handleOpenMapFromSlot(slotPopover.dayDate)} className="w-full flex items-center gap-2 p-2 hover:bg-gray-50 rounded-lg text-left transition-colors group" data-id="element-1927">
                  <div className="w-8 h-8 rounded-full bg-gray-100 text-gray-600 flex items-center justify-center group-hover:bg-navy group-hover:text-white transition-colors" data-id="element-1928">
                    <CalendarIcon size={14} data-id="element-1929" />
                  </div>
                  <div data-id="element-1930">
                    <p className="text-xs font-bold text-navy" data-id="element-1931">
                      Cerca per tutta la giornata
                    </p>
                    <p className="text-[10px] text-gray-500" data-id="element-1932">
                      Tutti gli slot liberi
                    </p>
                  </div>
                </button>
              </div>
            </div>
          </>}
      </div>;
  }
  // --- Map View ---
  return <div className="relative h-[calc(100vh-2rem)] w-full overflow-hidden bg-gray-50 font-inter" data-id="element-1933">
      <div className="absolute inset-0 z-0" data-id="element-1934">
        <MapContainer center={[41.9028, 12.4964]} zoom={13} zoomControl={false} style={{
        height: '100%',
        width: '100%'
      }} data-id="element-1935">
          <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png" data-id="element-1936" />
          <ZoomControl position="topright" data-id="element-1937" />
          <MapResizer data-id="element-1938" />
          <MapController bounds={mapBounds} data-id="element-1939" />

          {/* Confirmed location from initial steps */}
          {confirmedLocation && confirmedLocation.type === 'zona' && confirmedLocation.radius && <Circle center={[confirmedLocation.lat, confirmedLocation.lng]} radius={confirmedLocation.radius} pathOptions={{
          color: '#1E365C',
          weight: 2,
          opacity: 0.5,
          fillColor: '#1E365C',
          fillOpacity: 0.08,
          dashArray: '6, 8'
        }} data-id="element-1940" />}
          {confirmedLocation && confirmedLocation.type === 'indirizzo' && <Marker position={[confirmedLocation.lat, confirmedLocation.lng]} icon={createAddressMarkerIcon()} data-id="element-1941" />}

          {dayEvents.map(event => <Marker key={`event-${event.id}`} position={[event.lat, event.lng]} icon={createExistingEventIcon()} data-id="element-1942" />)}

          {/* Browse opportunities markers */}
          {step === 'browse-opportunities' && browseItems.map(item => <Marker key={`browse-${item.id}`} position={[item.lat, item.lng]} icon={createPlusIcon()} eventHandlers={{
          click: () => handleAddFromBrowse(item)
        }} data-id="element-1943" />)}

          {step === 'results' && packages[selectedGroupIndex] && (() => {
          const scheduledItems = scheduleAroundEvents(packages[selectedGroupIndex].items, dayEvents);
          const timeline = getMergedTimeline(scheduledItems);
          return <>
                  {/* Opportunity markers */}
                  {scheduledItems.map((item, index) => <Marker key={`opp-marker-${item.id}`} position={[item.lat, item.lng]} icon={createLetterIcon(String.fromCharCode(65 + index))} data-id="element-1944" />)}

                  {/* Polylines and arrows connecting all timeline entries in order */}
                  {timeline.map((entry, index) => {
              if (index === 0) return null;
              const prev = timeline[index - 1];
              const prevLat = prev.type === 'fixed' ? prev.event.lat : prev.item.lat;
              const prevLng = prev.type === 'fixed' ? prev.event.lng : prev.item.lng;
              const currLat = entry.type === 'fixed' ? entry.event.lat : entry.item.lat;
              const currLng = entry.type === 'fixed' ? entry.event.lng : entry.item.lng;
              if (prevLat === currLat && prevLng === currLng) return null;
              const angle = bearing(prevLat, prevLng, currLat, currLng);
              const arrowPos = interpolate(prevLat, prevLng, currLat, currLng, 0.93);
              return <Fragment key={`route-${index}`} data-id="element-1945">
                        <Polyline positions={[[prevLat, prevLng], [currLat, currLng]]} pathOptions={{
                  color: '#2E8C7E',
                  weight: 5,
                  opacity: 0.6,
                  dashArray: '1, 10',
                  lineCap: 'round'
                }} data-id="element-1946" />
                        <Marker position={arrowPos} icon={createArrowheadIcon(angle - 90)} interactive={false} data-id="element-1947" />
                      </Fragment>;
            })}
                </>;
        })()}

          {step === 'custom-compose' && <>
              {/* Opportunity markers with remove badge */}
              {customGroupItems.map((item, index) => <Fragment key={`compose-marker-${item.id}`} data-id="element-1948">
                  <Marker position={[item.lat, item.lng]} icon={createLetterIcon(String.fromCharCode(65 + index), true)} eventHandlers={{
              click: () => handleRemoveFromGroup(item)
            }} data-id="element-1949" />
                </Fragment>)}

              {/* Polylines and arrows connecting all timeline entries in order */}
              {(() => {
            const timeline = getMergedTimeline(customGroupItems);
            return timeline.map((entry, index) => {
              if (index === 0) return null;
              const prev = timeline[index - 1];
              const prevLat = prev.type === 'fixed' ? prev.event.lat : prev.item.lat;
              const prevLng = prev.type === 'fixed' ? prev.event.lng : prev.item.lng;
              const currLat = entry.type === 'fixed' ? entry.event.lat : entry.item.lat;
              const currLng = entry.type === 'fixed' ? entry.event.lng : entry.item.lng;
              if (prevLat === currLat && prevLng === currLng) return null;
              const angle = bearing(prevLat, prevLng, currLat, currLng);
              const arrowPos = interpolate(prevLat, prevLng, currLat, currLng, 0.93);
              return <Fragment key={`compose-route-${index}`} data-id="element-1950">
                      <Polyline positions={[[prevLat, prevLng], [currLat, currLng]]} pathOptions={{
                  color: '#2E8C7E',
                  weight: 5,
                  opacity: 0.6,
                  dashArray: '1, 10',
                  lineCap: 'round'
                }} data-id="element-1951" />
                      <Marker position={arrowPos} icon={createArrowheadIcon(angle - 90)} interactive={false} data-id="element-1952" />
                    </Fragment>;
            });
          })()}

              {/* Available items as plus icons */}
              {availableItems.map(item => <Marker key={item.id} position={[item.lat, item.lng]} icon={createPlusIcon()} eventHandlers={{
            click: () => handleAddToGroup(item)
          }} data-id="element-1953" />)}
            </>}
        </MapContainer>
      </div>

      {step === 'loading' && <div className="absolute inset-0 z-[500] bg-white/80 backdrop-blur-sm flex flex-col items-center justify-center animate-in fade-in duration-500" data-id="element-1954">
          <div className="bg-white p-8 rounded-2xl shadow-xl border border-gray-100 flex flex-col items-center max-w-sm text-center" data-id="element-1955">
            <div className="relative w-16 h-16 mb-6" data-id="element-1956">
              <div className="absolute inset-0 bg-teal-100 rounded-full animate-ping opacity-75" data-id="element-1957"></div>
              <div className="relative bg-teal-deep rounded-full w-16 h-16 flex items-center justify-center text-white" data-id="element-1958">
                <Sparkles size={32} className="animate-pulse" data-id="element-1959" />
              </div>
            </div>
            <h3 className="text-lg font-bold text-navy mb-2" data-id="element-1960">
              Analisi in corso...
            </h3>
            <p className="text-gray-500 text-sm mb-4" data-id="element-1961">
              Sto raggruppando le opportunità migliori per te vicino ai tuoi
              interventi.
            </p>
            <div className="w-full bg-gray-100 rounded-full h-1.5 overflow-hidden" data-id="element-1962">
              <div className="bg-teal-deep h-full rounded-full w-1/2 animate-[pulse_1.5s_ease-in-out_infinite]" data-id="element-1963"></div>
            </div>
          </div>
        </div>}

      {/* AI Results Modal */}
      {showAiModal && <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[500] flex items-center justify-center p-4 animate-in fade-in duration-300" data-id="element-1964">
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-8 text-center animate-in zoom-in-95 duration-300" data-id="element-1965">
            <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6 text-teal-deep" data-id="element-1966">
              <Sparkles size={32} data-id="element-1967" />
            </div>
            <h2 className="text-xl font-bold text-navy mb-2" data-id="element-1968">
              Ho creato {packages.length} gruppi di opportunità per te
            </h2>
            <p className="text-gray-500 mb-8" data-id="element-1969">
              Basati sulla tua posizione e disponibilità oraria.
            </p>
            <button onClick={() => setShowAiModal(false)} className="w-full bg-teal-deep text-white rounded-lg hover:bg-teal-deep/90 font-bold py-3 shadow-lg shadow-teal-deep/20 transition-all transform hover:scale-[1.02]" data-id="element-1970">
              Vedi sulla mappa
            </button>
          </div>
        </div>}

      {/* Toast */}
      {showToast && <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-[600] bg-green-600 text-white rounded-lg px-4 py-3 shadow-xl flex items-center gap-2 animate-in slide-in-from-bottom-4 fade-in duration-300" data-id="element-1971">
          <CheckCircle size={20} data-id="element-1972" />
          <span className="font-medium" data-id="element-1973">Gruppo creato con successo!</span>
        </div>}

      {/* Distance Warning Modal */}
      {showDistanceWarning && <div className="absolute inset-0 z-[600] bg-black/30 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-200" data-id="element-1974">
          <div className="bg-white rounded-xl shadow-2xl max-w-sm w-full p-6 animate-in zoom-in-95 duration-200" data-id="element-1975">
            <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mb-4 text-amber-600 mx-auto" data-id="element-1976">
              <AlertTriangle size={24} data-id="element-1977" />
            </div>
            <h3 className="text-lg font-bold text-navy text-center mb-2" data-id="element-1978">
              Attenzione: Distanza Elevata
            </h3>
            <p className="text-sm text-gray-600 text-center mb-6" data-id="element-1979">
              Alcuni interventi selezionati (Via Ottaviano, Via Candia) sono
              distanti dal punto di partenza. Vuoi confermare comunque questo
              gruppo?
            </p>
            <div className="flex gap-3" data-id="element-1980">
              <button onClick={() => setShowDistanceWarning(false)} className="flex-1 py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-50 rounded-lg border border-gray-200 transition-colors" data-id="element-1981">
                Modifica
              </button>
              <button onClick={handleConfirmGroup} className="flex-1 py-2.5 text-sm font-medium bg-teal-deep text-white rounded-lg hover:bg-teal-deep/90 shadow-sm transition-colors" data-id="element-1982">
                Conferma
              </button>
            </div>
          </div>
        </div>}

      {step === 'results' || step === 'custom-compose' || step === 'browse-opportunities'}

      {/* Browse Opportunities Panel */}
      {step === 'browse-opportunities' && <div className="absolute top-4 left-4 bottom-4 w-[360px] bg-white rounded-xl border border-gray-200 shadow-xl overflow-hidden flex flex-col z-[400] animate-in slide-in-from-left-4 duration-500" data-id="element-1983">
          <div className="p-4 border-b border-gray-200 bg-white" data-id="element-1984">
            <div className="flex items-center justify-between mb-3" data-id="element-1985">
              <h2 className="font-bold text-navy text-lg font-montserrat" data-id="element-1986">
                Opportunità
              </h2>
              <span className="bg-teal-deep text-white text-xs font-bold px-2.5 py-0.5 rounded-full" data-id="element-1987">
                {browseItems.length}
              </span>
            </div>

            {/* Date Navigator */}
            <div className="flex items-center gap-3 mb-3" data-id="element-1988">
              <button onClick={() => handleDateNav('prev')} className="p-1 hover:bg-gray-100 rounded-md text-gray-400 hover:text-navy transition-colors" data-id="element-1989">
                <ChevronLeft size={18} data-id="element-1990" />
              </button>
              <span className="text-sm font-bold text-navy flex-1 text-center" data-id="element-1991">
                {selectedDate.getDate()} {months[selectedDate.getMonth()]}{' '}
                {selectedDate.getFullYear()}
              </span>
              <button onClick={() => handleDateNav('next')} className="p-1 hover:bg-gray-100 rounded-md text-gray-400 hover:text-navy transition-colors" data-id="element-1992">
                <ChevronRight size={18} data-id="element-1993" />
              </button>
            </div>

            {/* Search */}
            <div className="relative" data-id="element-1994">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" data-id="element-1995" />
              <input type="text" placeholder="Cerca indirizzo..." value={browseSearchQuery} onChange={e => setBrowseSearchQuery(e.target.value)} className="w-full pl-9 pr-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-deep focus:border-transparent outline-none" data-id="element-1996" />
            </div>
          </div>

          {/* Fixed events for this date */}
          {dayEvents.length > 0 && <div className="px-4 pt-3 pb-1" data-id="element-1997">
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2" data-id="element-1998">
                I tuoi interventi fissi
              </p>
              {dayEvents.map(event => <div key={event.id} className="flex items-center gap-3 p-2.5 bg-gray-50 border border-gray-200 rounded-lg mb-2" data-id="element-1999">
                  <div className="w-5 h-5 rounded-full bg-navy text-white flex items-center justify-center flex-shrink-0" data-id="element-2000">
                    <CalendarIcon size={10} data-id="element-2001" />
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-2002">
                    <p className="font-bold text-navy text-xs truncate" data-id="element-2003">
                      {event.address}
                    </p>
                    <p className="text-[10px] text-gray-500" data-id="element-2004">{event.time}</p>
                  </div>
                  <div className="px-1.5 py-0.5 bg-navy/10 rounded text-[9px] font-bold text-navy uppercase tracking-wider" data-id="element-2005">
                    Fisso
                  </div>
                </div>)}
            </div>}

          {/* Available opportunities list */}
          <div className="flex-1 overflow-y-auto p-3 space-y-2 bg-gray-50/50" data-id="element-2006">
            {(() => {
          const filtered = browseItems.filter(item => browseSearchQuery ? item.address.toLowerCase().includes(browseSearchQuery.toLowerCase()) : true);
          return filtered.length > 0 ? filtered.map(item => <div key={item.id} className="flex items-center gap-3 p-3 bg-white border border-gray-200 rounded-xl hover:border-teal-deep/40 hover:shadow-sm transition-all group" data-id="element-2007">
                    <div className="w-8 h-8 rounded-full bg-teal-deep/10 flex items-center justify-center flex-shrink-0" data-id="element-2008">
                      <MapPin size={14} className="text-teal-deep" data-id="element-2009" />
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-2010">
                      {/* 1. Service type as title */}
                      <p className="text-sm font-bold text-navy mb-0.5" data-id="element-2011">
                        {item.serviceType}
                      </p>
                      {/* 2. Duration • time window */}
                      <div className="flex items-center gap-1 text-xs text-gray-500 mb-0.5" data-id="element-2012">
                        <span className="font-bold text-teal-deep" data-id="element-2013">
                          {formatDuration(getDuration(item.timeWindow))}
                        </span>
                        <span className="text-gray-300" data-id="element-2014">•</span>
                        <span data-id="element-2015">{item.timeWindow}</span>
                      </div>
                      {/* 3. Address */}
                      <p className="text-[11px] text-gray-400 truncate mb-1" data-id="element-2016">
                        {item.address}
                      </p>
                      {/* 4. Urgency */}
                      <UrgencyBadgeComponent level={item.urgency} data-id="element-2017" />
                    </div>
                    <button onClick={() => handleAddFromBrowse(item)} className="w-8 h-8 bg-gray-50 hover:bg-teal-50 text-gray-400 hover:text-teal-deep rounded-lg flex items-center justify-center transition-colors group-hover:bg-teal-50 group-hover:text-teal-deep" data-id="element-2018">
                      <Plus size={16} data-id="element-2019" />
                    </button>
                  </div>) : <div className="text-center py-8 px-4" data-id="element-2020">
                  <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-2" data-id="element-2021">
                    <Search className="text-gray-400 w-5 h-5" data-id="element-2022" />
                  </div>
                  <p className="text-xs text-gray-500" data-id="element-2023">
                    Nessuna opportunità trovata
                  </p>
                </div>;
        })()}
          </div>

          {/* Footer CTAs */}
          <div className="p-4 border-t border-gray-200 bg-white space-y-2" data-id="element-2024">
            <button onClick={handleAiGroupFromBrowse} disabled={browseItems.length === 0} className="w-full bg-teal-deep text-white rounded-lg hover:bg-teal-deep/90 font-medium text-sm py-3 shadow-sm transition-colors flex items-center justify-center gap-2 disabled:opacity-50" data-id="element-2025">
              <Sparkles size={16} data-id="element-2026" />
              Pianifica con CleanAI
            </button>
            <button onClick={handleManualGroupFromBrowse} disabled={browseItems.length === 0} className="w-full bg-white border border-gray-200 text-navy rounded-lg hover:bg-gray-50 font-medium text-sm py-3 shadow-sm transition-colors flex items-center justify-center gap-2 disabled:opacity-50" data-id="element-2027">
              <Edit2 size={16} data-id="element-2028" />
              Crea gruppo manuale
            </button>
          </div>
        </div>}

      {step === 'results' && <div className="absolute top-4 left-4 bottom-4 w-[360px] bg-white rounded-xl border border-gray-200 shadow-xl overflow-hidden flex flex-col z-[400] animate-in slide-in-from-left-4 duration-500" data-id="element-2029">
          <div className="p-4 border-b border-gray-200 flex justify-between items-center bg-white" data-id="element-2030">
            <div className="flex items-center gap-2" data-id="element-2031">
              <button onClick={() => setSelectedGroupIndex(prev => Math.max(0, prev - 1))} disabled={selectedGroupIndex === 0} className="p-1 rounded-md hover:bg-gray-100 disabled:opacity-30 transition-colors" data-id="element-2032">
                <ChevronLeft size={18} data-id="element-2033" />
              </button>
              <h2 className="font-bold text-navy text-sm" data-id="element-2034">
                Opportunità {selectedGroupIndex + 1} di {packages.length}
              </h2>
              <button onClick={() => setSelectedGroupIndex(prev => Math.min(packages.length - 1, prev + 1))} disabled={selectedGroupIndex === packages.length - 1} className="p-1 rounded-md hover:bg-gray-100 disabled:opacity-30 transition-colors" data-id="element-2035">
                <ChevronRight size={18} data-id="element-2036" />
              </button>
            </div>
            <button onClick={handleStartCompose} className="flex items-center gap-1.5 bg-teal-deep text-white rounded-lg hover:bg-teal-deep/90 font-medium text-xs px-3 py-2 transition-colors shadow-sm" data-id="element-2037">
              <Plus size={14} data-id="element-2038" />
              Crea
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-0" data-id="element-2039">
            {(() => {
          const timelineArray = getMergedTimeline(scheduleAroundEvents(packages[selectedGroupIndex].items, dayEvents));
          return <div className="relative" data-id="element-2040">
                  {/* Dashed vertical line from first to last indicator */}
                  {timelineArray.length > 1 && <div className="absolute left-[11px] top-[12px] bottom-[12px] w-px border-l-2 border-dashed border-gray-300" style={{
              zIndex: 0
            }} data-id="element-2041" />}

                  {timelineArray.map((entry, index, array) => {
              // Calculate travel time from previous entry
              let dist = 0;
              let walkTime = 0;
              if (index > 0) {
                const prev = array[index - 1];
                const curr = entry;
                const prevLat = prev.type === 'fixed' ? prev.event.lat : prev.item.lat;
                const prevLng = prev.type === 'fixed' ? prev.event.lng : prev.item.lng;
                const currLat = curr.type === 'fixed' ? curr.event.lat : curr.item.lat;
                const currLng = curr.type === 'fixed' ? curr.event.lng : curr.item.lng;
                dist = Math.round(haversineDistance(prevLat, prevLng, currLat, currLng));
                walkTime = roundUpTo5(Math.ceil(dist / 80));
              }
              return <div key={entry.type === 'fixed' ? `fixed-${entry.event.id}` : `opp-${entry.item.id}`} data-id="element-2042">
                        {/* Travel chip between entries */}
                        {index > 0 && <div className="flex" data-id="element-2043">
                            <div className="w-6 flex-shrink-0" data-id="element-2044" />
                            <div className="flex-1 flex items-center justify-center py-2 ml-3" data-id="element-2045">
                              <div className="bg-teal-deep/5 border border-teal-deep/20 rounded-xl px-4 py-2 flex items-center justify-center gap-3 shadow-sm" data-id="element-2046">
                                <div className="flex items-center gap-1.5" data-id="element-2047">
                                  <Navigation size={14} className="text-teal-deep" data-id="element-2048" />
                                  <span className="text-sm font-bold text-teal-deep" data-id="element-2049">
                                    {walkTime} min
                                  </span>
                                </div>
                                <div className="w-px h-3.5 bg-teal-deep/20" data-id="element-2050"></div>
                                <span className="text-[11px] text-gray-500" data-id="element-2051">
                                  {dist >= 1000 ? `${(dist / 1000).toFixed(1)} km` : `${dist} m`}
                                </span>
                              </div>
                            </div>
                          </div>}

                        {/* Entry row: indicator + card */}
                        <div className="flex gap-3 items-center relative" style={{
                  zIndex: 1
                }} data-id="element-2052">
                          {/* Indicator */}
                          {entry.type === 'fixed' ? <div className="w-6 h-6 rounded-full bg-navy text-white flex items-center justify-center text-[10px] font-bold border-2 border-white shadow-sm flex-shrink-0" data-id="element-2053">
                              <CalendarIcon size={12} data-id="element-2054" />
                            </div> : <div className="w-6 h-6 rounded-full bg-teal-deep text-white flex items-center justify-center text-xs font-bold border-2 border-white shadow-sm flex-shrink-0" data-id="element-2055">
                              {entry.letter}
                            </div>}

                          {/* Card */}
                          {entry.type === 'fixed' ? <div className="flex-1 bg-gray-50 border border-gray-200 rounded-lg p-3 opacity-75" data-id="element-2056">
                              <p className="font-bold text-navy text-sm" data-id="element-2057">
                                {entry.event.address}
                              </p>
                              <p className="text-xs text-gray-500" data-id="element-2058">
                                {entry.event.time}
                              </p>
                            </div> : <div className="flex-1 bg-white border border-gray-200 rounded-xl p-3 shadow-sm hover:shadow-md transition-shadow relative group" data-id="element-2059">
                              <p className="text-sm font-bold text-navy mb-1" data-id="element-2060">
                                {entry.item.serviceType}
                              </p>
                              <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-1" data-id="element-2061">
                                <Clock size={12} className="text-teal-deep" data-id="element-2062" />
                                <span className="font-bold text-teal-deep" data-id="element-2063">
                                  {formatDuration(getDuration(entry.item.timeWindow))}
                                </span>
                                <span className="text-gray-300" data-id="element-2064">•</span>
                                <span data-id="element-2065">{entry.item.timeWindow}</span>
                              </div>
                              <div className="flex items-center gap-1 text-[11px] text-gray-400 mb-2" data-id="element-2066">
                                <MapPin size={10} className="flex-shrink-0" data-id="element-2067" />
                                <span className="truncate" data-id="element-2068">
                                  {entry.item.address}
                                </span>
                              </div>
                              <div className="flex items-center justify-between" data-id="element-2069">
                                <UrgencyBadgeComponent level={entry.item.urgency} data-id="element-2070" />
                                <button onClick={() => setSelectedJobForDetail(entry.item)} className="text-xs font-medium text-teal-deep hover:text-teal-deep/80 flex items-center gap-0.5 transition-colors" data-id="element-2071">
                                  Dettagli
                                  <ChevronRightIcon size={14} data-id="element-2072" />
                                </button>
                              </div>
                            </div>}
                        </div>
                      </div>;
            })}
                </div>;
        })()}
          </div>

          <div className="p-4 border-t border-gray-200 bg-white space-y-2" data-id="element-2073">
            <button onClick={handleStartCompose} className="w-full bg-white border border-gray-200 text-navy rounded-lg hover:bg-gray-50 font-medium text-sm py-3 shadow-sm transition-colors flex items-center justify-center gap-2" data-id="element-2074">
              <Edit2 size={16} data-id="element-2075" />
              Modifica gruppo
            </button>

            {packages[selectedGroupIndex]?.isCustom && <>
                {showDeleteConfirm ? <div className="flex gap-2" data-id="element-2076">
                    <button onClick={() => setShowDeleteConfirm(false)} className="flex-1 bg-white border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 font-medium text-sm py-3 transition-colors" data-id="element-2077">
                      Annulla
                    </button>
                    <button onClick={handleDeleteGroup} className="flex-1 bg-red-500 text-white rounded-lg hover:bg-red-600 font-medium text-sm py-3 shadow-sm transition-colors flex items-center justify-center gap-2" data-id="element-2078">
                      <Trash2 size={14} data-id="element-2079" />
                      Conferma
                    </button>
                  </div> : <button onClick={() => setShowDeleteConfirm(true)} className="w-full bg-red-50 border border-red-100 text-red-600 rounded-lg hover:bg-red-100 font-medium text-sm py-3 shadow-sm transition-colors flex items-center justify-center gap-2" data-id="element-2080">
                    <Trash2 size={16} data-id="element-2081" />
                    Elimina gruppo
                  </button>}
              </>}
          </div>
        </div>}

      {step === 'custom-compose' && <div className="absolute top-4 left-4 bottom-4 w-[360px] bg-white rounded-xl border border-gray-200 shadow-xl overflow-hidden flex flex-col z-[400] animate-in slide-in-from-left-4 duration-500" data-id="element-2082">
          <div className="p-4 border-b border-gray-200 bg-white" data-id="element-2083">
            <h2 className="font-bold text-navy text-lg" data-id="element-2084">Il Tuo Gruppo</h2>
            <p className="text-xs text-gray-500" data-id="element-2085">
              {customGroupItems.length} interventi selezionati
            </p>
          </div>

          <div className="flex-1 overflow-y-auto bg-gray-50/50" data-id="element-2086">
            <div className="p-4 space-y-0" data-id="element-2087">
              {(() => {
            const timeline = getMergedTimeline(customGroupItems);
            return <div className="relative" data-id="element-2088">
                    {/* Dashed vertical line from first to last indicator */}
                    {timeline.length > 1 && <div className="absolute left-[11px] top-[12px] bottom-[12px] w-px border-l-2 border-dashed border-gray-300" style={{
                zIndex: 0
              }} data-id="element-2089" />}

                    {timeline.map((entry, index, array) => {
                // Calculate travel time from previous entry
                let dist = 0;
                let walkTime = 0;
                if (index > 0) {
                  const prev = array[index - 1];
                  const curr = entry;
                  const prevLat = prev.type === 'fixed' ? prev.event.lat : prev.item.lat;
                  const prevLng = prev.type === 'fixed' ? prev.event.lng : prev.item.lng;
                  const currLat = curr.type === 'fixed' ? curr.event.lat : curr.item.lat;
                  const currLng = curr.type === 'fixed' ? curr.event.lng : curr.item.lng;
                  dist = Math.round(haversineDistance(prevLat, prevLng, currLat, currLng));
                  walkTime = roundUpTo5(Math.ceil(dist / 80));
                }
                const isDragging = entry.type === 'opportunity' && draggedItemId === entry.item.id;
                const isDropTarget = dropTargetIndex === index;
                return <div key={entry.type === 'fixed' ? `fixed-${entry.event.id}` : `opp-${entry.item.id}`} onDragOver={e => handleTimelineDragOver(e, index)} onDrop={e => handleTimelineDrop(e, index)} data-id="element-2090">
                          {/* Drop indicator line */}
                          {isDropTarget && draggedItemId !== null && <div className="flex items-center gap-2 py-1 mb-2 animate-in fade-in duration-150" data-id="element-2091">
                              <div className="h-0.5 flex-1 bg-teal-deep rounded-full" data-id="element-2092"></div>
                              <span className="text-[10px] font-bold text-teal-deep uppercase tracking-wider" data-id="element-2093">
                                Rilascia qui
                              </span>
                              <div className="h-0.5 flex-1 bg-teal-deep rounded-full" data-id="element-2094"></div>
                            </div>}

                          {/* Travel chip between entries */}
                          {index > 0 && !isDropTarget && <div className="flex" data-id="element-2095">
                              <div className="w-6 flex-shrink-0" data-id="element-2096" />
                              <div className="flex-1 flex items-center justify-center py-2 ml-3" data-id="element-2097">
                                <div className="bg-teal-deep/5 border border-teal-deep/20 rounded-xl px-4 py-2 flex items-center justify-center gap-3 shadow-sm" data-id="element-2098">
                                  <div className="flex items-center gap-1.5" data-id="element-2099">
                                    <Navigation size={14} className="text-teal-deep" data-id="element-2100" />
                                    <span className="text-sm font-bold text-teal-deep" data-id="element-2101">
                                      {walkTime} min
                                    </span>
                                  </div>
                                  <div className="w-px h-3.5 bg-teal-deep/20" data-id="element-2102"></div>
                                  <span className="text-[11px] text-gray-500" data-id="element-2103">
                                    {dist >= 1000 ? `${(dist / 1000).toFixed(1)} km` : `${dist} m`}
                                  </span>
                                </div>
                              </div>
                            </div>}

                          {/* Entry row: indicator + card */}
                          {entry.type === 'fixed' ? <div className="flex gap-3 items-center relative" style={{
                    zIndex: 1
                  }} onDragOver={e => handleTimelineDragOver(e, index)} onDrop={e => handleTimelineDrop(e, index)} data-id="element-2104">
                              <div className="w-6 h-6 rounded-full bg-navy text-white flex items-center justify-center text-[10px] font-bold flex-shrink-0" data-id="element-2105">
                                <CalendarIcon size={12} data-id="element-2106" />
                              </div>
                              <div className="flex-1 flex items-center gap-3 p-3 bg-white border border-gray-200 rounded-xl opacity-75" data-id="element-2107">
                                <div className="flex-1 min-w-0" data-id="element-2108">
                                  <div className="flex items-center gap-1.5" data-id="element-2109">
                                    <p className="font-bold text-navy text-sm truncate" data-id="element-2110">
                                      {entry.event.address}
                                    </p>
                                    {entry.event.source === 'google' ? <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/a/a5/Google_Calendar_icon_%282020%29.svg/500px-Google_Calendar_icon_%282020%29.svg.png" alt="Google" className="w-3.5 h-3.5 object-contain flex-shrink-0" data-id="element-2111" /> : entry.event.source === 'outlook' ? <img src="https://img.icons8.com/color/600/outlook-calendar.png" alt="Outlook" className="w-3.5 h-3.5 object-contain flex-shrink-0" data-id="element-2112" /> : null}
                                  </div>
                                  <p className="text-xs text-gray-500" data-id="element-2113">
                                    {entry.event.time}
                                  </p>
                                </div>
                                <div className="px-1.5 py-0.5 bg-navy/10 rounded text-[9px] font-bold text-navy uppercase tracking-wider" data-id="element-2114">
                                  Fisso
                                </div>
                              </div>
                            </div> : <div className="flex gap-3 items-center relative" style={{
                    zIndex: 1
                  }} data-id="element-2115">
                              <div className="w-6 h-6 rounded-full bg-teal-deep text-white flex items-center justify-center text-xs font-bold border-2 border-white shadow-sm flex-shrink-0" data-id="element-2116">
                                {entry.letter}
                              </div>
                              <div draggable onDragStart={e => handleTimelineDragStart(e, entry.item.id)} onDragEnd={handleTimelineDragEnd} className={`flex-1 flex items-start gap-2.5 p-3 bg-white border rounded-xl shadow-sm group transition-all cursor-grab active:cursor-grabbing ${isDragging ? 'opacity-40 border-dashed border-teal-deep scale-95' : 'border-gray-200 hover:shadow-md'}`} data-id="element-2117">
                                <div className="text-gray-300 hover:text-navy transition-colors mt-0.5" data-id="element-2118">
                                  <GripVertical size={16} data-id="element-2119" />
                                </div>
                                <div className="flex-1 min-w-0" data-id="element-2120">
                                  <p className="text-sm font-bold text-navy mb-0.5" data-id="element-2121">
                                    {entry.item.serviceType}
                                  </p>
                                  <div className="flex items-center gap-1 text-xs text-gray-500 mb-0.5" data-id="element-2122">
                                    <span className="font-bold text-teal-deep" data-id="element-2123">
                                      {formatDuration(getDuration(entry.item.originalTimeWindow || entry.item.timeWindow))}
                                    </span>
                                    <span className="text-gray-300" data-id="element-2124">•</span>
                                    <span data-id="element-2125">{entry.item.timeWindow}</span>
                                  </div>
                                  <p className="text-[11px] text-gray-400 truncate mb-1" data-id="element-2126">
                                    {entry.item.address}
                                  </p>
                                  <UrgencyBadgeComponent level={entry.item.urgency} data-id="element-2127" />
                                </div>
                                <button onClick={() => handleRemoveFromGroup(entry.item)} className="text-gray-400 hover:text-red-500 p-1 rounded-full hover:bg-red-50 transition-colors mt-0.5" data-id="element-2128">
                                  <Minus size={16} data-id="element-2129" />
                                </button>
                              </div>
                            </div>}

                          {/* Drop zone after fixed events */}
                          {entry.type === 'fixed' && draggedItemId !== null && <div className="h-8 flex items-center justify-center mt-1" onDragOver={e => {
                    e.preventDefault();
                    setDropTargetIndex(index + 1);
                  }} onDrop={e => handleTimelineDrop(e, index + 1)} data-id="element-2130">
                              {dropTargetIndex === index + 1 ? <div className="flex items-center gap-2 w-full animate-in fade-in duration-150" data-id="element-2131">
                                  <div className="h-0.5 flex-1 bg-teal-deep rounded-full" data-id="element-2132"></div>
                                  <span className="text-[10px] font-bold text-teal-deep uppercase tracking-wider" data-id="element-2133">
                                    Rilascia qui
                                  </span>
                                  <div className="h-0.5 flex-1 bg-teal-deep rounded-full" data-id="element-2134"></div>
                                </div> : <div className="h-0.5 w-3/4 bg-gray-200 rounded-full opacity-50" data-id="element-2135"></div>}
                            </div>}
                        </div>;
              })}
                  </div>;
          })()}

              {/* Drop zone at the end of timeline */}
              {draggedItemId !== null && <div className="relative min-h-[100px] py-4" onDragOver={e => {
            e.preventDefault();
            e.dataTransfer.dropEffect = 'move';
            setDropTargetIndex(getMergedTimeline(customGroupItems).length);
          }} onDrop={e => handleTimelineDrop(e, getMergedTimeline(customGroupItems).length)} data-id="element-2136">
                  {dropTargetIndex === getMergedTimeline(customGroupItems).length && <div className="flex items-center gap-2 py-1 mb-2 animate-in fade-in duration-150" data-id="element-2137">
                      <div className="h-0.5 flex-1 bg-teal-deep rounded-full" data-id="element-2138"></div>
                      <span className="text-[10px] font-bold text-teal-deep uppercase tracking-wider" data-id="element-2139">
                        Rilascia qui
                      </span>
                      <div className="h-0.5 flex-1 bg-teal-deep rounded-full" data-id="element-2140"></div>
                    </div>}
                </div>}
            </div>

            <div className="px-4 py-2 flex items-center gap-3" onDragOver={draggedItemId !== null ? e => {
          e.preventDefault();
          e.dataTransfer.dropEffect = 'move';
          setDropTargetIndex(getMergedTimeline(customGroupItems).length);
        } : undefined} onDrop={draggedItemId !== null ? e => handleTimelineDrop(e, getMergedTimeline(customGroupItems).length) : undefined} data-id="element-2141">
              <div className="h-px bg-gray-200 flex-1" data-id="element-2142"></div>
              <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider" data-id="element-2143">
                Opportunità disponibili
              </span>
              <div className="h-px bg-gray-200 flex-1" data-id="element-2144"></div>
            </div>

            <div className="p-4 space-y-2" data-id="element-2145">
              {availableItems.map(item => <div key={item.id} className="flex items-center gap-3 p-3 bg-white border border-gray-200 rounded-xl hover:border-teal-deep/50 transition-colors" data-id="element-2146">
                  <div className="w-6 h-6 rounded-full border-2 border-gray-200 flex items-center justify-center flex-shrink-0" data-id="element-2147">
                    <div className="w-2 h-2 bg-gray-300 rounded-full" data-id="element-2148"></div>
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-2149">
                    {/* 1. Service type as title */}
                    <p className="text-sm font-bold text-navy mb-0.5" data-id="element-2150">
                      {item.serviceType}
                    </p>
                    {/* 2. Duration • time window */}
                    <div className="flex items-center gap-1 text-xs text-gray-500 mb-0.5" data-id="element-2151">
                      <span className="font-bold text-teal-deep" data-id="element-2152">
                        {formatDuration(getDuration(item.timeWindow))}
                      </span>
                      <span className="text-gray-300" data-id="element-2153">•</span>
                      <span data-id="element-2154">{item.timeWindow}</span>
                    </div>
                    {/* 3. Address */}
                    <p className="text-[11px] text-gray-400 truncate mb-1" data-id="element-2155">
                      {item.address}
                    </p>
                    {/* 4. Urgency */}
                    <UrgencyBadgeComponent level={item.urgency} data-id="element-2156" />
                  </div>
                  <button onClick={() => handleAddToGroup(item)} className="w-8 h-8 bg-gray-50 hover:bg-teal-50 text-gray-500 hover:text-teal-deep rounded-lg flex items-center justify-center transition-colors" data-id="element-2157">
                    <Plus size={16} data-id="element-2158" />
                  </button>
                </div>)}
            </div>
          </div>

          <div className="p-4 border-t border-gray-200 bg-white" data-id="element-2159">
            <div className="flex justify-between items-center mb-3 text-xs text-gray-500" data-id="element-2160">
              <span data-id="element-2161">{customGroupItems.length} interventi</span>
              <span data-id="element-2162">~45 min spostamenti</span>
            </div>
            <button onClick={handleConfirmGroup} className="w-full bg-teal-deep text-white rounded-lg hover:bg-teal-deep/90 font-medium text-sm py-3 shadow-sm transition-colors flex items-center justify-center gap-2" data-id="element-2163">
              <CheckCircle size={16} data-id="element-2164" />
              Conferma Gruppo
            </button>
          </div>
        </div>}

      <JobDetailModal isOpen={!!selectedJobForDetail} onClose={() => setSelectedJobForDetail(null)} job={selectedJobForDetail} data-id="element-2165" />
    </div>;
}